"use strict";

const APP_ID = "5284869";
const CREATE_BUNDLE_TRANSACTION_API = "https://api-service.loopwork.co/bundleTransaction";
const FILTER_BUNDLE_SP_API = "https://apiv2.loopwork.co/bundleStorefront/filterBundleSellingPlanIds";
const GET_PRODUCT_BUNDLE_DATA_API = "https://apiv2.loopwork.co/bundleStorefront/getChildVariants?id=";
const PRODUCT_QUANTITY_SELECTOR = ".quantity__input";
const GET_PREPAID_SELLING_PLAN_DATA_URL =
    "https://api.loopwork.co/api/merchant/sellingPlanGroups/sellingPlansWithDetails?shopifyIds=";


const productDataClass = "loopProductQuickJson",
    loopSubscriptionContainerId = "loop-subscription-container",
    oneTimePurchaseOptionClass = "loop-one-time-purchase-option",
    subscriptionGroupClass = "loop-subscription-group",
    purchaseOptionName = "loop_purchase_option",
    sellingPlanSelectorClass = "loop-selling-plan-selector";

const useCompareAtPrice = true; //change this to false to use price instead of compare_at_price
const myShopifyDomain = window.Shopify.shop;
const baseUrl = `${APP_ID === "5186329"
    ? "https://staging-cdn.loopwork.co"
    : "https://cdn.loopwork.co"
    }/${myShopifyDomain}`;

/**
 * start application
 */
async function initLoopWidget(productId) {
    try {
        log(`start application: ${productId}`);
        showLoopSkeletonLoader(productId);
        const productData = getLoopProductData(productId);
        setupLoopDomListeners(productId);
        initializeWindowLoopProps();
        const loopSellingPlanGroups = getLoopSellingPlanGroups(
            productData?.selling_plan_groups
        );
        productData.selling_plan_groups = loopSellingPlanGroups;
        const loopProductVariants = getLoopProductVariants(
            productData.variants,
            loopSellingPlanGroups
        );
        productData.variants = loopProductVariants;
        window.loopProps[productId] = {
            product: productData,
        };
        generateLoopWidgetVariantSPMaps(productId);
        // Load store configurations and UI properties in parallel to speed up initial load
        await Promise.all([
            fetchStoreJson(productId),
            setLoopUIProperties(Shopify.shop)
        ]);

        // Initialize and display widget UI immediately once settings are loaded
        showSellingPlanFieldsetLoop(productId);
        showLoopPurchaseOptionsLabel(productId);
        initializeLoopUI(productData);
        // comment listenLoopCustomEvent call to stop listening loop preset bundle add to cart event
        listenLoopCustomEvent();
        hideDifferentVariantSellingPlansLoop(productData);
        displayLoopWidget(productId);
        injectOrUpdateKcTryOnceLink(productId, findSelectedVariantLoop(productId));
        observeFormChangesLoop(productData);
        hideLoopSkeletonLoader(productId);

        // Process bundle settings and exclusions in the background without blocking widget display
        Promise.all([
            processLoopBundleProduct(productId),
            getLoopBundleSpgs(productId)
        ]).catch(error => logError(error));
    } catch (error) {
        logError(error);
        hideLoopSkeletonLoader(productId);
    }
}

/* ============================================================
   KC CUSTOM FUNCTIONS — WIDGET INIT
   - renderKCRewards       : Populates reward grid tiles
   - renderKCTestimonial   : Populates testimonial sections per plan
   ============================================================ */


/**
 * Populates .kc-testimonial-section elements from the template wrapper.
 * Runs on every init/plan change to show the correct plan's testimonial.
 */
function renderKCTestimonial(productId) {
    // Remove previously injected dynamic sections
    document.querySelectorAll(".kc-bottom_label[data-dynamic='true']").forEach(el => el.remove());

    const buttonTemplate = document.querySelector('#kc-testimonial-button-template');
    if (!buttonTemplate) return;

    const variant = findSelectedVariantLoop(productId);
    const loopPropsProduct = window.loopProps?.[productId];
    if (!loopPropsProduct || !variant) return;

    const isVisibleLabel = (el, templateEl) => {
        if (el.getAttribute('data-dynamic') === 'true') return false;
        if (templateEl && el.contains(templateEl)) return false;
        return true;
    };

    const staticBottomLabels = Array.from(document.querySelectorAll('.kc-bottom_label'))
        .filter(el => isVisibleLabel(el, buttonTemplate));

    // Inject button HTML into static bottom label containers
    const btnHtml = buttonTemplate.innerHTML;
    staticBottomLabels.forEach(label => {
        if (label.dataset.baseContent === btnHtml) return;
        label.dataset.baseContent = btnHtml;
        label.innerHTML = btnHtml;
        label.style.display = '';
    });
}

function widgetLogger(message, ...additionalData) {
    const prefix = "%cLoop Widget: ";
    let style = `background-color: #7D41FF; color: #FFFFFF; padding: 2px;`;
    console.log(prefix + message, style, ...additionalData);
}

/**
 * setup DOM listeners to update values like selling_plan, price etc.
 * @param {number} productId
 */
function setupLoopDomListeners(productId) {
    log(`setup dom listeners for ${productId}`);
    const parentContainer = getLoopSubscriptionContainer(productId);
    const oneTimeOptions = parentContainer.querySelectorAll(
        `.${oneTimePurchaseOptionClass}`
    );
    const sellingPlanGroupOptions = parentContainer.querySelectorAll(
        `.${subscriptionGroupClass}`
    );
    const purchaseOptions = parentContainer.querySelectorAll(
        `input[name=${purchaseOptionName}]`
    );
    const deliveryOptions = parentContainer.querySelectorAll(
        `.${sellingPlanSelectorClass}`
    );

    for (const option of oneTimeOptions) {
        option.addEventListener("click", clickOnSellingPlanGroupContainerLoop);
    }

    for (const option of sellingPlanGroupOptions) {
        option.addEventListener("click", clickOnSellingPlanGroupContainerLoop);
    }

    for (const option of purchaseOptions) {
        option.addEventListener("click", changeInSellingPlanGroupLoop);
    }

    for (const option of deliveryOptions) {
        option.addEventListener("change", changeInDeliveryOptionLoop);
    }
    log(`setup dom listeners complete for ${productId}`);
}

/**
 * initialize props to use at multiple places
 */
function initializeWindowLoopProps() {
    if (!window.loopProps) {
        window.loopProps = {};
    }
}

/**
 * returns loop subscriptions selling plan groups
 * @param {Array} sellingPlanGroups
 * @returns
 */
function getLoopSellingPlanGroups(sellingPlanGroups) {
    if (Array.isArray(sellingPlanGroups)) {
        return sellingPlanGroups.filter((s) => s.app_id === APP_ID);
    }
    return [];
}

/**
 * returns product variants with loop selling plan groups
 * @param {Array} variants
 * @param {Array} loopSellingPlanGroups
 * @returns
 */
function getLoopProductVariants(variants, loopSellingPlanGroups) {
    const loopSpgSet = new Set(loopSellingPlanGroups.map((s) => s.id));
    return variants.map((v) => {
        return {
            ...v,
            selling_plan_allocations: v.selling_plan_allocations.filter((s) =>
                loopSpgSet.has(s.selling_plan_group_id)
            ),
        };
    });
}

const isCacheExpired = (timestamp) => {
    const oneHour = 60 * 60 * 1000;
    return !timestamp || Date.now() - timestamp > oneHour;
};

function generateLoopWidgetVariantSPMaps(productId) {
    const variantToSellingPlanGroups = {};
    const variantToSellingPlans = {};
    const sellingPlanGroupToSellingPlans = {};

    window.loopProps[productId]["product"].variants.forEach((variant) => {
        variantToSellingPlanGroups[variant.id] = [];
        variantToSellingPlans[variant.id] = {};

        variant.selling_plan_allocations.forEach((allocation) => {
            if (
                !variantToSellingPlanGroups[variant.id].includes(
                    allocation.selling_plan_group_id
                )
            ) {
                variantToSellingPlanGroups[variant.id].push(
                    allocation.selling_plan_group_id
                );
            }
            if (
                !variantToSellingPlans[variant.id][
                allocation.selling_plan_group_id
                ]
            ) {
                variantToSellingPlans[variant.id][
                    allocation.selling_plan_group_id
                ] = [];
            }
            variantToSellingPlans[variant.id][
                allocation.selling_plan_group_id
            ].push(allocation.selling_plan_id);
            if (
                !sellingPlanGroupToSellingPlans[
                allocation.selling_plan_group_id
                ]
            ) {
                sellingPlanGroupToSellingPlans[
                    allocation.selling_plan_group_id
                ] = [];
            }
            if (
                !sellingPlanGroupToSellingPlans[
                    allocation.selling_plan_group_id
                ].includes(allocation.selling_plan_id)
            ) {
                sellingPlanGroupToSellingPlans[
                    allocation.selling_plan_group_id
                ].push(allocation.selling_plan_id);
            }
        });
    });
    window.loopProps[productId][
        "variantToSellingPlanGroups"
    ] = variantToSellingPlanGroups;
    window.loopProps[productId][
        "variantToSellingPlans"
    ] = variantToSellingPlans;
    window.loopProps[productId][
        "sellingPlanGroupToSellingPlans"
    ] = sellingPlanGroupToSellingPlans;
}

function loopWidgetHasCommonElements(arr1, arr2) {
    const set1 = new Set(arr1);
    return arr2.some((elem) => set1.has(elem));
}

function getCommonElements(arr1 = [], arr2 = []) {
    const set1 = new Set(arr1);
    return arr2.filter((elem) => set1.has(elem));
}

function getStoreDefaultPlanFromPrepaidV2(plans) {
    const data = Object.entries(plans)
        .filter(([key, value]) => value.isDefault === true)
        .map(([key, value]) => Number(key));
    return data;
}

function getStoreDefaultSellingPlanShopifyIds(productId) {
    if (
        window.loopProps[productId]["storeJson"].hasPrepaid &&
        window.loopProps[productId]["prepaidSellingPlans"]
    ) {
        return getStoreDefaultPlanFromPrepaidV2(
            window.loopProps[productId]["prepaidSellingPlans"]
        );
    }
    return (
        window.loopProps[productId]["storeJson"]
            .storeDefaultSellingPlanShopifyIds ?? []
    );
}

function selectDefaultPlanForAllGroups(productId, selectedVariantId) {
    const variantSellingPlanGroups =
        window.loopProps[productId]["variantToSellingPlans"][selectedVariantId];
    const storeDefaultSellingPlanShopifyIds = getStoreDefaultSellingPlanShopifyIds(
        productId
    );
    for (const groupId in variantSellingPlanGroups) {
        const groupSpIds = variantSellingPlanGroups[groupId];
        if (groupSpIds.length) {
            const defaultSellingPlanShopifyId = getCommonElements(
                storeDefaultSellingPlanShopifyIds,
                groupSpIds
            )?.[0];
            changeDropdownValueBySelectId(
                `loop-select-${selectedVariantId}-${groupId}`,
                defaultSellingPlanShopifyId
            );
            if (
                window.loopProps[productId]?.sellingPlanId &&
                window.loopProps[productId]?.sellingPlanGroupId === groupId
            ) {
                changeInDeliveryOptionLoopV2(
                    productId,
                    defaultSellingPlanShopifyId
                );
            }
        }
    }
}

function changeDropdownValueBySelectId(selectId, value) {
    if (!value || !selectId) return;
    const selectElement = document.getElementById(selectId);
    if (selectElement) {
        selectElement.value = `${value}`;
    }
}

const fetchWithCacheControl = async (url, key) => {
    const lastFetch = localStorage.getItem(`loop_cdn_timestamp_${key}`);

    if (isCacheExpired(lastFetch)) {
        localStorage.setItem(`loop_cdn_timestamp_${key}`, Date.now());

        return fetch(url, {
            method: "GET",
            cache: "reload",
            mode: "cors",
            credentials: "same-origin",
            headers: {
                Accept: "application/json",
            },
        });
    }

    return fetch(url, {
        method: "GET",
        cache: "default",
        mode: "cors",
        credentials: "same-origin",
        headers: {
            Accept: "application/json",
        },
    });
};

async function fetchStoreJson(productId) {
    try {
        const cacheKey = `loop_store_json_${myShopifyDomain}`;
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                window.loopProps[productId].storeJson = JSON.parse(cached);
                return;
            }
        } catch (e) { }

        const storesRes = await fetchWithCacheControl(
            `${baseUrl}/store.json`,
            "store"
        );
        if (!storesRes) {
            throw new Error("Cannot connect to Loop widget CDN");
        }

        const storeJson = await storesRes.json();
        if (!storeJson) {
            throw new Error("Cannot fetch store data");
        }

        window.loopProps[productId].storeJson = { ...storeJson };
        try {
            sessionStorage.setItem(cacheKey, JSON.stringify(storeJson));
        } catch (e) { }
    } catch (error) {
        window.loopProps[productId].storeJson = null;
    }
}

async function setLoopUIProperties(shopifyDomain) {
    const loopUIProps = await fetchLoopUIProperties(shopifyDomain);
    window.loopPropsUI = {
        ...loopUIProps,
    };
}

async function fetchLoopUIProperties(shopifyDomain) {
    const cacheKey = `loop_ui_props_${shopifyDomain}`;
    try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            return JSON.parse(cached);
        }
    } catch (e) { }

    log(`fetch loop subscription UI props: ${shopifyDomain}`);
    const endpoint = `https://d217z8zw4dqir.cloudfront.net/${shopifyDomain}.json`;
    const response = await fetch(endpoint);
    const data = (await response.json()) ?? {};

    try {
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (e) { }

    return data;
}

// for showing fieldset where selling plans are present
function showSellingPlanFieldsetLoop(productId) {
    const loopSubscriptionWidget = getLoopSubscriptionContainer(
        productId
    ).querySelector("#loop-selling-plan-fieldset");
    if (loopSubscriptionWidget) {
        loopSubscriptionWidget.classList.remove(
            "loop-display-none",
            "loop-display-none-by-variant"
        );
    }
}

// show Purchase Option label
function showLoopPurchaseOptionsLabel(productId) {
    const elements = getLoopSubscriptionContainer(productId).querySelectorAll(
        ".loop-purchase-options-label"
    );
    if (elements) {
        for (const e of elements) {
            e.classList.remove(
                "loop-display-none",
                "loop-display-none-by-variant"
            );
        }
    }
}

function getLoopVariantId(productId) {
    return (
        window.loopProps[productId].selectedVariantId ||
        getVariantIdFromURLLoop(window.loopProps[productId].variants) ||
        getFirstAvailableVariantVariantIdLoop(productId)
    );
}

function initializeLoopUI(productData) {
    let variantId = getVariantIdFromURLLoop(productData.variants);
    if (!variantId) {
        variantId = getFirstAvailableVariantVariantIdLoop(productData.id);
    }
    loopInit({ productId: productData.id, product: productData, variantId });
}

function getVariantIdFromURLLoop(variants) {
    const currentPageUrl = document.URL;
    const url = new URL(currentPageUrl);
    const variantIdFromUrl = url.searchParams.get("variant") || "";
    const variantIdSet = new Set(variants?.map((v) => v.id));

    return variantIdSet.has(Number(variantIdFromUrl)) ? variantIdFromUrl : null;
}

function getFirstAvailableVariantVariantIdLoop(productId) {
    const productData = getLoopProductData(productId);
    const v = productData?.variants.find((v) => v.available);
    const variantId = v?.id;
    return variantId;
}

function displayLoopWidget(productId) {
    const loopWidget = getLoopSubscriptionContainer(productId);
    if (loopWidget) {
        loopWidget.classList.remove("loop-display-none");
    }
}

function observeFormChangesLoop(productData) {
    document.querySelectorAll("form").forEach((form) => {
        const variantElement = form.querySelector('[name="id"]');
        const loopVariantElement = form.querySelector(
            '[name="loop_variant_id"]'
        );
        if (loopVariantElement && variantElement?.value) {
            loopVariantElement.value = variantElement.value;
        }

        const variantIdSet = new Set(productData?.variants?.map((v) => v.id));
        if (
            variantElement?.value &&
            variantIdSet.has(Number(variantElement?.value))
        ) {
            const config = {
                attributes: true,
                childList: true,
                subtree: true,
            };

            const callback = (mutationsList, observer) => {
                const variantId = variantElement?.value ?? "";
                const loopVariantElementId = loopVariantElement?.value ?? "";
                if (
                    variantId &&
                    loopVariantElement &&
                    variantId !== loopVariantElementId
                ) {
                    loopVariantElement.value = variantId;
                    loopVariantChanged({ loopProduct: productData, variantId });
                }
            };

            const observer = new MutationObserver(callback);
            observer.observe(form, config);
        }
    });
}

/// *********************************** Legacy functions ***********************************************

//Global functions

// classes for showing price
const loopPriceSelectors = [
    ".price:not(.price--on-sale) .price__regular .price-item--regular",
    ".price.price--on-sale .price__sale .price-item--sale",
    ".product-single__prices .product__price:not(.product__price--compare)",
    ".product-pricing .product--price .price--main .money",
    "[data-zp-product-discount-price]",
    ".product-single__header .product__price",
    ".modal_price .current_price",
    ".product-area__col--price .current-price.theme-money",
    '[data-product-type="price"][data-pf-type="ProductPrice"]',
    ".product__price .fs-heading-4-base[data-price]",
    "#product-price .money[data-product-price]",
    "#ProductPrice",
    ".product-single__price",
    ".price:not(.price--on-sale) span.price-item--regular",
    ".product-price .price .money:not(.original)",
    ".price-box #price .price",
    ".product__price span[data-product-price]",
    ".product-form--price-wrapper .product-form--price",
    ".product-page--pricing--variant-price #price-field",
    ".price-reviews .product-price",
];

// makes a map object of instances of a key
const arrToInstanceCountObjLoop = (arr) =>
    arr.reduce((obj, e) => {
        obj[e] = (obj[e] || 0) + 1;
        return obj;
    }, {});

/**
 * returns variant from from window loop props
 * if selected then returns selected variant else first variant from loop props
 * @param {*} productId
 * @param {*} variantId
 * @returns
 */
function findSelectedVariantLoop(productId, variantId) {
    const product =
        getProductFromLoopProps(productId) || getLoopProductData(productId);
    const selectedVariantId = determineSelectedVariantIdLoop(
        variantId,
        productId,
        product
    );
    return findVariantByIdLoop(product, selectedVariantId);
}

function getProductFromLoopProps(productId) {
    return window.loopProps?.[productId]?.product;
}

function determineSelectedVariantIdLoop(variantId, productId, product) {
    if (variantId) return variantId;
    if (window.loopProps?.[productId]?.selectedVariantId)
        return window.loopProps[productId].selectedVariantId;
    return product.variants?.[0]?.id;
}

function findVariantByIdLoop(product, variantId) {
    return product.variants?.find(
        (variant) => Number(variant.id) === Number(variantId)
    );
}

/**
 * clicking the selected variant after 300ms
 * @param {*} variant
 * @param {*} productId
 */
function defaultSelectFirstSellingPlanLoop(variant, productId) {
    const loopPurchaseOptionsContainer = getLoopSubscriptionContainer(
        productId
    );
    const loopPurchaseOptions =
        loopPurchaseOptionsContainer.querySelectorAll(
            "input[name=loop_purchase_option]"
        ) || [];

    let isFirstOption = true;
    const { selling_plan_allocations } = variant;
    const spgIds =
        selling_plan_allocations?.map((spg) => spg.selling_plan_group_id) || [];

    loopPurchaseOptions.forEach((element) => {
        if (
            Number(element.dataset.variantId) === Number(variant.id) &&
            (window.loopProps[productId].previousSPGId &&
                spgIds.includes(window.loopProps[productId].previousSPGId)
                ? element.dataset.id ==
                window.loopProps[productId].previousSPGId
                : isFirstOption)
        ) {
            isFirstOption = false;
            element.checked = true;

            setTimeout(() => {
                if (loopIsTouchDevice()) {
                    const { dataset } = element;
                    changeInSellingPlanGroupLoopMobile(
                        dataset.id,
                        dataset.name,
                        dataset.productId
                    );
                }
                element.click();
            }, 300);
        }
    });
}

// hides Purchase Option label
function hideLoopPurchaseOptionsLabel(productId) {
    const elements = getLoopSubscriptionContainer(productId).querySelectorAll(
        ".loop-purchase-options-label"
    );
    if (elements) {
        for (const e of elements) {
            e.classList.add(
                "loop-display-none",
                "loop-display-none-by-variant"
            );
        }
    }
}

// adds Purchase Option label text
function addLoopPurchaseOptionLabelText(productId) {
    let elements = getLoopSubscriptionContainer(productId).querySelectorAll(
        ".loop-purchase-options-label"
    );
    if (elements) {
        elements.forEach((element) => {
            if (element) {
                const hasCustom = element.querySelector(".continuum-desc, .continuum-eyebrow");
                if (!hasCustom) {
                    element.innerHTML = `${window.loopPropsUI.loopPurchaseOptionslabel ||
                        "Purchase Options"
                        }`;
                }
            }
        });
    }
}

// One time purchase label text
function addLoopOneTimePurchaseOptionLabelText(productId) {
    let elements = getLoopSubscriptionContainer(productId).querySelectorAll(
        ".loop-one-time-purchase-option-label"
    );
    if (elements) {
        elements.forEach((element) => {
            if (element) {
                element.innerHTML = `${window.loopPropsUI.loopOneTimePurchaselabel ||
                    "One time Purchase"
                    }`;
            }
        });
    }
}

/**
 * Renders One time purchase option at bottom
 * @param {*} productId
 */
function showOneTimePurchaseOptionAtBottomLoop(productId) {
    const loopContainer = getLoopSubscriptionContainer(productId);

    const elementAtTop = loopContainer.querySelector(
        "#loop-one-time-purchase-option-at-top"
    );
    const elementAtBottom = loopContainer.querySelector(
        "#loop-one-time-purchase-option-at-bottom"
    );

    if (elementAtTop && elementAtBottom && elementAtTop.innerHTML) {
        elementAtBottom.innerHTML = elementAtTop.innerHTML;
        elementAtTop.innerHTML = "";
    }

    const loopSubscriptionGroupElements = loopContainer.querySelectorAll(
        ".loop-subscription-group"
    );
    loopSubscriptionGroupElements.forEach((element) => {
        element.classList.remove("loop-subscription-group-border-top");
        element.classList.add("loop-subscription-group-border-bottom");
    });
}

/**
 * Hides "Each" label in Price
 */
function hideEachLabelForPriceLoop() {
    const selectors = [
        ".loop-subscription-group-price-quantity",
        ".loop-one-time-purchase-option-price-quantity",
    ];

    selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
            element.classList.add("loop-display-none");
        });
    });
}

/**
 * for displaying tooltip => removes loop-display-none class
 * @param {*} productId
 * @returns
 */
function displayLoopTooltip(productId) {
    const loopContainer = getLoopSubscriptionContainer(productId);
    const tooltipElement = loopContainer.querySelector("#loop-tooltip");

    if (!tooltipElement) return;

    tooltipElement.classList.remove("loop-display-none");

    updateLoopTooltipContent(
        loopContainer,
        "#loop-tooltip-label",
        window.loopPropsUI.subscriptionPopupLabel
    );
    updateLoopTooltipContent(
        loopContainer,
        "#loop-tooltip-description",
        window.loopPropsUI.subscriptionPopupDescription
    );

    const label = loopContainer.querySelector("#loop-tooltip-label");
    const description = loopContainer.querySelector(
        "#loop-tooltip-description"
    );

    if (label && description) {
        label.style.fill = window.getComputedStyle(description).color;
    }
}

function updateLoopTooltipContent(container, selector, content) {
    const element = container.querySelector(selector);
    if (element && content) {
        element.innerHTML = content;
    }
}

/**
 * for hiding tooltip => adds loop-display-none class
 * @param {*} productId
 */
function hideLoopTooltip(productId) {
    const element = getLoopSubscriptionContainer(productId).querySelector(
        "#loop-tooltip"
    );
    if (element) {
        element.classList.add("loop-display-none");
    }
}

// adds extra css styles and classes on loop-style
function addExtraLoopStyles() {
    if (window && window.loopPropsUI && window.loopPropsUI.style) {
        let classList = {
            purchase_option_label: [],
            widget_feildset: [".loop-selling-plan-fieldset"],
            selling_plan_group_container: [],
            selling_plan_group_label: [],
            selling_plan_label: [".loop-selling-plan-selector-label"],
            selling_plan_selector: [".loop-selling-plan-selector"],
            selling_plan_price_label: [],
            selling_plan_price_subtitle_label: [
                ".loop-one-time-purchase-option-price-quantity",
                ".loop-subscription-group-price-quantity",
            ],
            selling_plan_description_label: [],
            selling_plan_discount_badge_style: [
                ".loop-subscription-group-discount-badge",
            ],
            subscription_details_label: [".loop-tooltip-label"],
            subscription_details_popup: [
                ".loop-tooltiptext",
                ".loop-container-arrow",
                ".loop-tooltip-description",
            ],
            selling_plan_group_selected: [".loop-selected-selling-plan-group"],
            selling_plan_group_radio: [
                ".loop-subscription-group-radio",
            ],
        };

        const getProperties = ({ id, data }) => {
            if (data) {
                let keys = Object.keys(data);
                let properties = "";
                keys.forEach((key) => {
                    let value = data[key];
                    properties = ` ${properties} ${key}: ${value} !important;`;
                });
                return properties;
            } else {
                return "";
            }
        };

        const getClassName = ({ id, data }) => {
            return classList[id] || [];
        };

        let extraClasses = ``;
        const { style } = window.loopPropsUI;
        style.map((st) => {
            let classNames = getClassName(st);
            classNames.map((className) => {
                extraClasses =
                    extraClasses +
                    `
                 ${className} {
                     ${getProperties(st)}
                 }
             `;
            });
        });

        let loopStyles = document.querySelectorAll(".loop-style");
        if (loopStyles) {
            loopStyles.forEach((element) => {
                element.innerHTML = `${element.innerHTML}
                 ${extraClasses}
             `;
            });
        }
    }

    if (window && window.loopPropsUI && window.loopPropsUI.cssClassess) {
        let loopStyles = document.querySelectorAll(".loop-style");
        if (loopStyles) {
            loopStyles.forEach((element) => {
                element.innerHTML = `${element.innerHTML}
                 ${window.loopPropsUI.cssClassess}
             `;
            });
        }
    }
}

// for hiding fieldset where selling plans are present
function hideLoopSellingPlanFieldset(productId) {
    const loopSubscriptionWidget = getLoopSubscriptionContainer(
        productId
    ).querySelector("#loop-selling-plan-fieldset");
    if (loopSubscriptionWidget) {
        loopSubscriptionWidget.classList.add(
            "loop-display-none",
            "loop-display-none-by-variant"
        );
    }
}

// Changes based on loopPropsUI
function applyLoopSettings({ productId }) {
    let product = window.loopProps[productId].product;
    const variant = findSelectedVariantLoop(productId);
    const hasCustomLabel = getLoopSubscriptionContainer(productId)?.querySelector(
        ".loop-purchase-options-label .continuum-desc, .loop-purchase-options-label .continuum-eyebrow"
    );

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.displayLoopPurchaseOptionLabel === false &&
        !hasCustomLabel
    ) {
        hideLoopPurchaseOptionsLabel(productId);
    }

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.loopPurchaseOptionslabel
    ) {
        addLoopPurchaseOptionLabelText(productId);
    }

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.loopOneTimePurchaselabel
    ) {
        addLoopOneTimePurchaseOptionLabelText(productId);
    }

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.displayOneTimePurchaseOptionAtBottom
    ) {
        showOneTimePurchaseOptionAtBottomLoop(productId);

        const loopOneTimeOptions = getLoopSubscriptionContainer(
            productId
        ).querySelectorAll(".loop-one-time-purchase-option");
        loopOneTimeOptions.forEach((option) => {
            option.addEventListener(
                "click",
                clickOnSellingPlanGroupContainerLoop
            );
        });
        const loopPurchaseOptions = getLoopSubscriptionContainer(
            productId
        ).querySelectorAll("input[name=loop_purchase_option]");
        loopPurchaseOptions.forEach((option) => {
            option.addEventListener("click", changeInSellingPlanGroupLoop);
        });
    }

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.displayEachLabelForPrice === false
    ) {
        hideEachLabelForPriceLoop();
    }

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.hidePlanSelectorIfOnlyOne
    ) {
        const { availableSellingPlanAllocations = [] } = window.loopProps[
            productId
        ];
        let ids = [];
        availableSellingPlanAllocations.map((a) => {
            ids.push(a.selling_plan_group_id);
        });

        let idCount = arrToInstanceCountObjLoop(ids);
        Object.keys(idCount).forEach((key) => {
            let plan = idCount[key];
            if (plan === 1) {
                let id = `#loop-selling-plan-container-${variant.id}-${key}`;
                let parentElement = document.querySelector(id);
                if (parentElement) {
                    let label = parentElement.querySelector(
                        ".loop-selling-plan-selector-label"
                    );
                    let labelPlan = parentElement.querySelector(
                        `#loop-selling-plan-first-delivery-options-${variant.id}-${key}`
                    );
                    let planSelector = parentElement.querySelector(
                        ".loop-selling-plan-selector"
                    );
                    if (label) {
                        label.classList.add("loop-display-none");
                    }
                    if (labelPlan) {
                        labelPlan.classList.add("loop-display-none");
                    }
                    if (planSelector) {
                        planSelector.classList.add("loop-display-none");
                    }
                }
            }
        });
    }

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.showPlanSelectorAsTextIfOnlyOnePlan &&
        !window.loopPropsUI.hidePlanSelectorIfOnlyOne
    ) {
        const { availableSellingPlanAllocations = [] } = window.loopProps[
            productId
        ];
        let ids = [];
        availableSellingPlanAllocations.map((a) => {
            ids.push(a.selling_plan_group_id);
        });

        let idCount = arrToInstanceCountObjLoop(ids);
        Object.keys(idCount).forEach((key) => {
            let plan = idCount[key];
            if (plan === 1) {
                let id = `#loop-selling-plan-first-delivery-options-${variant.id}-${key}`;
                let element = document.querySelector(id);
                if (element && element.classList) {
                    element.classList.remove("loop-display-none");
                }
                id = `#loop-select-${variant.id}-${key}`;
                element = document.querySelector(id);
                if (element) {
                    element.classList.add("loop-display-none");
                }
            }
        });
    }

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.hideWholeWidgetIfOnlyOnePlan
    ) {
        if (product.requires_selling_plan) {
            //check if only for selling plan
            if (
                variant.selling_plan_allocations &&
                variant.selling_plan_allocations.length === 1
            ) {
                //has only 1 selling plan
                hideLoopSellingPlanFieldset(productId);
                hideLoopPurchaseOptionsLabel(productId);
            }
        }
    }

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.hideRadioButtonIfOnlyOnePlan
    ) {
        if (product.requires_selling_plan) {
            //check if only for selling plan

            const { availableSellingPlanAllocations } = window.loopProps[
                productId
            ];
            let ids = [];
            availableSellingPlanAllocations.map((a) => {
                ids.push(a.selling_plan_group_id);
            });
            let idCount = arrToInstanceCountObjLoop(ids);
            let onlyOneSellingPlanGroup = false;
            if (Object.keys(idCount).length === 1) {
                onlyOneSellingPlanGroup = true;
            } else {
                onlyOneSellingPlanGroup = false;
            }

            if (onlyOneSellingPlanGroup) {
                //has only 1 selling plan

                let loopSubscriptionGroupRadio = getLoopSubscriptionContainer(
                    productId
                ).querySelectorAll(".loop-subscription-group-radio");
                if (loopSubscriptionGroupRadio) {
                    loopSubscriptionGroupRadio.forEach((element) => {
                        element.classList.add("loop-display-none");
                    });
                }
                let elements = getLoopSubscriptionContainer(
                    productId
                ).querySelectorAll(
                    `.loop-subscription-group-selling-plans-container`
                );
                if (elements) {
                    elements.forEach((element) => {
                        element.classList.add("loop-left-margin-0");
                    });
                }
            }
        }
    }

    addExtraLoopStyles();

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.displaySubscriptionPopup &&
        variant &&
        variant.selling_plan_allocations &&
        variant.selling_plan_allocations.length
    ) {
        displayLoopTooltip(productId);
    } else {
        hideLoopTooltip(productId);
    }

    if (
        product &&
        product.requires_selling_plan &&
        Array.isArray(variant.selling_plan_allocations) &&
        variant.selling_plan_allocations.length
    ) {
        let parentId = `#loop-product-variant-${variant.id}`;
        let parentElement = getLoopSubscriptionContainer(
            productId
        ).querySelector(parentId);

        if (
            window &&
            window.loopPropsUI &&
            window.loopPropsUI.displayOneTimePurchaseOptionAtBottom
        ) {
            let id = `.loop-subscription-group`;
            let elements = parentElement.querySelectorAll(id);
            if (elements && elements.length) {
                let last = elements[elements.length - 1];
                last.style.borderBottom = "0";
                last.classList.remove("loop-subscription-group-border-bottom");
            }
        } else {
            let id = `.loop-subscription-group`;
            let elements = parentElement.querySelectorAll(id);
            if (elements && elements.length) {
                let first = elements[0];
                first.style.borderTop = "0";
                first.classList.remove("loop-subscription-group-border-top");
            }
        }
    }

    if (
        window &&
        window.loopPropsUI &&
        window.loopPropsUI.displayDiscountBadge
    ) {
        displayLoopDiscountBadge({ productId });
    } else {
        let loopSubscriptionDiscountBadge = document.querySelectorAll(
            ".loop-subscription-group-discount-badge"
        );
        if (loopSubscriptionDiscountBadge) {
            loopSubscriptionDiscountBadge.forEach((element) => {
                if (element) {
                    element.classList.add("loop-display-none");
                }
            });
        }
    }

    if (window && window.loopPropsUI && window.loopPropsUI.translationData) {
        let translationData = window.loopPropsUI.translationData || {};
        let mapElements = {
            widget_price_label_text: [
                ".loop-one-time-purchase-option-price-quantity",
                ".loop-subscription-group-price-quantity",
            ],
        };

        Object.keys(mapElements).forEach((key) => {
            if (translationData && translationData[key]) {
                let elementIds = mapElements[key];
                elementIds.map((id) => {
                    let elements = document.querySelectorAll(id);
                    if (elements) {
                        elements.forEach((element) => {
                            element.innerText = translationData[key];
                        });
                    }
                });
            }
        });
    }
}

/**
 * clicking the first purchase option on selling plan group change
 * @param {*} event
 * @returns
 */
function clickOnSellingPlanGroupContainerLoop(event) {
    const container =
        event.target.closest(".loop-subscription-group") ||
        event.target.closest(".loop-one-time-purchase-option");

    if (!container) return;

    const radio = container.querySelector('input[type="radio"]');
    const selectedPlanGroupId =
        window.loopProps[radio.dataset.productId]?.sellingPlanGroupId;

    if (radio?.dataset?.id !== selectedPlanGroupId) {
        radio.click();
    }
}

// on variant change
function loopVariantChanged({ loopProduct, variantId }) {
    loopInit({
        productId: loopProduct.id,
        product: JSON.parse(JSON.stringify(loopProduct)),
        variantId,
    });
}

// hides or shows loop-widget
function checkVariantsSellingPlanAllocationLoop(variant, productId) {
    const hasSellingPlans = variant?.selling_plan_allocations?.length > 0;
    if (hasSellingPlans) {
        //display loop widget
        showSellingPlanFieldsetLoop(productId);
        showLoopPurchaseOptionsLabel(productId);
    } else {
        //hide loop widget
        hideLoopSellingPlanFieldset(productId);
        hideLoopPurchaseOptionsLabel(productId);
    }
}

/**
 * makes one time purchase option selected
 * @param {*} variant
 * @param {*} productId
 */
function defaultSelectOneTimePurchaseOptionLoop(variant, productId) {
    const onetimeCheckRadioLoop = getLoopSubscriptionContainer(
        productId
    ).querySelector(`#loop-one-time-purchase-${productId}`);

    if (onetimeCheckRadioLoop) {
        onetimeCheckRadioLoop.checked = true;
        onetimeCheckRadioLoop.click();

        if (loopIsTouchDevice()) {
            const { dataset } = onetimeCheckRadioLoop;
            changeInSellingPlanGroupLoopMobile(
                dataset.id,
                dataset.name,
                dataset.productId
            );
        }
    } else {
        defaultSelectFirstSellingPlanLoop(variant, productId);
    }
}

/**
 * handles variant display and apply loop settings
 * @param {Object} param0
 */
function loopInit({ productId, product, variantId }) {
    updateLoopProperties({ product, productId, variantId });
    const selectedVariant = findSelectedVariantLoop(productId, variantId);
    toggleVariantDisplayLoop(product, selectedVariant.id);
    checkVariantsSellingPlanAllocationLoop(selectedVariant, productId);
    applyLoopSettings({ productId });
    applyDefaultSelectionBasedOnLoopSettings(selectedVariant, productId);
    hideLoopBundleSPG(productId);
}

function toggleVariantDisplayLoop(product, selectedVariantId) {
    product.variants.forEach((variant) => {
        const displayStyle =
            variant.id === selectedVariantId ? "block" : "none";
        document.querySelector(
            `#loop-product-variant-${variant.id}`
        ).style.display = displayStyle;
    });
}

function applyDefaultSelectionBasedOnLoopSettings(selectedVariant, productId) {
    const loopContainer = getLoopSubscriptionContainer(productId);
    const themeDefaultToSubscription = loopContainer ? loopContainer.dataset.defaultToSubscription === 'true' : false;

    const shouldDefaultToSubscription = themeDefaultToSubscription ||
        window.loopPropsUI?.byDefaultChooseSubscriptionOption;

    if (shouldDefaultToSubscription) {
        defaultSelectFirstSellingPlanLoop(selectedVariant, productId);
        selectDefaultPlanForAllGroups(productId, selectedVariant.id);
    } else {
        defaultSelectOneTimePurchaseOptionLoop(selectedVariant, productId);
    }

    if (isProductBundle(productId)) {
        handleBundleWidgetVisibility(productId);
    }
}

// makes the chosen selling plan selected
function updateSelectDropDownDefaultValuesLoop({
    productId,
    variant,
    sellingPlanGroupId,
}) {
    const sellingPlanGroups =
        window.loopProps[productId].product.selling_plan_groups;

    if (!Array.isArray(sellingPlanGroups) || !sellingPlanGroups.length) {
        return;
    }

    sellingPlanGroups.forEach((spg) => {
        // if (sellingPlanGroupId !== spg.id) {
        resetSelectDropdownLoop(variant.id, spg.id);
        // }
    });
}

function resetSelectDropdownLoop(variantId, sellingPlanGroupId) {
    const selectTag = document.getElementById(
        `loop-select-${variantId}-${sellingPlanGroupId}`
    );
    if (selectTag) {
        selectTag.options[0].selected = true;
    }
}

//for touch devices
function changeInSellingPlanGroupLoopMobile(
    sellingPlanGroupId,
    sellingPlanGroupName,
    productId
) {
    if (!window.loopProps[productId].previousSPG) {
        window.loopProps[productId].previousSPGId = sellingPlanGroupId;
    }
    const variant = findSelectedVariantLoop(
        productId,
        window.loopProps[productId].selectedVariantId
    );
    let sellingPlans =
        variant.selling_plan_allocations.filter(
            (spa) => spa.selling_plan_group_id === sellingPlanGroupId
        ) || [];
    let sellingPlan =
        sellingPlans && sellingPlans.length ? sellingPlans[0] : {};
    let sellingPlanId = sellingPlan.selling_plan_id;
    updateLoopProperties({
        productId,
        variantId: variant.id,
        sellingPlanGroupId,
        sellingPlanGroupName,
        sellingPlanId,
        sellingPlan,
    });
    updateSelectDropDownDefaultValuesLoop({
        productId,
        variant,
        sellingPlanGroupId: sellingPlanGroupId,
    });
    updatePriceInParentElementsLoop({ productId });
    updateLoopSellingPlanDescriptionUI({ productId });
    displayLoopDiscountBadge({ productId });
    updateCartButtonTextLoop({ productId });
    updatePriceInUILoop({ productId });
    updatePrepaidPriceInUILoop({ productId });
    updateDynamicReturnOnCareLabels(productId);
    applyLoopBundleDiscount(productId);
    checkAllowCheckoutIfBundle(productId);
    let removeElementId = ".loop-selected-selling-plan-group";
    let elements = getLoopSubscriptionContainer(productId).querySelectorAll(
        removeElementId
    );
    if (elements) {
        elements.forEach((element) => {
            if (element) {
                element.classList.remove("loop-selected-selling-plan-group");
            }
        });
    }
    if (sellingPlanGroupId === "loop-one-time-purchase") {
        let elements = getLoopSubscriptionContainer(productId).querySelectorAll(
            ".loop-one-time-purchase-option"
        );
        if (elements) {
            elements.forEach((element) => {
                element.classList.add("loop-selected-selling-plan-group");
            });
        }
    } else {
        let elementId = `#loop-${variant.id}-${sellingPlanGroupId}`;
        let element = getLoopSubscriptionContainer(productId).querySelector(
            elementId
        );
        if (element) {
            element.classList.add("loop-selected-selling-plan-group");
        }
    }

    selectDefaultPlanForAllGroups(productId, variant.id);
}

// on change of selling plan group
function changeInSellingPlanGroupLoop(option) {
    let sellingPlanGroupId = option.target.dataset.id;
    let sellingPlanGroupName = option.target.dataset.name;
    let productId = option.target.dataset.productId;
    if (!window.loopProps[productId].previousSPG) {
        window.loopProps[productId].previousSPGId = sellingPlanGroupId;
    }
    const variant = findSelectedVariantLoop(
        productId,
        window.loopProps[productId].selectedVariantId
    );
    let sellingPlans =
        variant.selling_plan_allocations.filter(
            (spa) => spa.selling_plan_group_id === sellingPlanGroupId
        ) || [];
    let sellingPlan =
        sellingPlans && sellingPlans.length ? sellingPlans[0] : {};
    let sellingPlanId = sellingPlan.selling_plan_id;
    updateLoopProperties({
        productId,
        variantId: variant.id,
        sellingPlanGroupId,
        sellingPlanGroupName,
        sellingPlanId,
        sellingPlan,
    });
    updateSelectDropDownDefaultValuesLoop({
        productId,
        variant,
        sellingPlanGroupId: option.target.dataset.id,
    });
    updatePriceInParentElementsLoop({ productId });
    updateLoopSellingPlanDescriptionUI({ productId });
    displayLoopDiscountBadge({ productId });
    updateCartButtonTextLoop({ productId });
    updatePriceInUILoop({ productId });
    updatePrepaidPriceInUILoop({ productId });
    updateDynamicReturnOnCareLabels(productId);
    applyLoopBundleDiscount(productId);
    checkAllowCheckoutIfBundle(productId);

    let removeElementId = ".loop-selected-selling-plan-group";
    let elements = getLoopSubscriptionContainer(productId).querySelectorAll(
        removeElementId
    );
    if (elements) {
        elements.forEach((element) => {
            if (element) {
                element.classList.remove("loop-selected-selling-plan-group");
            }
        });
    }
    if (sellingPlanGroupId === "loop-one-time-purchase") {
        let elements = getLoopSubscriptionContainer(productId).querySelectorAll(
            ".loop-one-time-purchase-option"
        );
        if (elements) {
            elements.forEach((element) => {
                element.classList.add("loop-selected-selling-plan-group");
            });
        }
    } else {
        let elementId = `#loop-${variant.id}-${sellingPlanGroupId}`;
        let element = getLoopSubscriptionContainer(productId).querySelector(
            elementId
        );
        if (element) {
            element.classList.add("loop-selected-selling-plan-group");
        }
    }
    selectDefaultPlanForAllGroups(productId, variant.id);
}

function changeInDeliveryOptionLoop(option) {
    let sellingPlanId = option.target.value;
    let productId = option.target.dataset.productId;
    updateLoopProperties({ productId, sellingPlanId });
    updatePriceInParentElementsLoop({ productId });
    updateLoopSellingPlanDescriptionUI({ productId });
    displayLoopDiscountBadge({ productId });
    updatePriceInUILoop({ productId });
    updatePrepaidPriceInUILoop({ productId });
    updateDynamicReturnOnCareLabels(productId);
    applyLoopBundleDiscount(productId);
    checkAllowCheckoutIfBundle(productId);
}

function changeInDeliveryOptionLoopV2(productId, sellingPlanId) {
    updateLoopProperties({ productId, sellingPlanId });
    updatePriceInParentElementsLoop({ productId });
    updateLoopSellingPlanDescriptionUI({ productId });
    displayLoopDiscountBadge({ productId });
    updatePriceInUILoop({ productId });
    updatePrepaidPriceInUILoop({ productId });
    updateDynamicReturnOnCareLabels(productId);
    applyLoopBundleDiscount(productId);
    checkAllowCheckoutIfBundle(productId);
}

// discount badge handling
function displayLoopDiscountBadge({ productId }) {
    const prepaidPlansData =
        window.loopProps[productId]?.prepaidSellingPlans || {};

    const variant = findSelectedVariantLoop(productId);
    const { selling_plan_groups } = window.loopProps[productId].product;
    if (window && window.loopProps && window.loopPropsUI.displayDiscountBadge) {
        selling_plan_groups.map((spg) => {
            const { selling_plans } = spg;
            const firstSp = selling_plans[0];
            let deliveryFreq =
                prepaidPlansData[firstSp.id]?.deliveriesPerBillingCycle || 1;

            let discountList = [];
            selling_plans.map((sp) => {
                const delFreq =
                    prepaidPlansData[sp.id]?.deliveriesPerBillingCycle || 1;
                const { price_adjustments } = sp;
                let priceAdj = price_adjustments.length
                    ? price_adjustments[0]
                    : {};
                discountList.push({
                    value: priceAdj.value / delFreq,
                    value_type: priceAdj.value_type,
                    amount:
                        priceAdj.value_type === "fixed_amount"
                            ? priceAdj.value
                            : priceAdj.value_type === "price"
                                ? Number(variant.price) -
                                Number(priceAdj.value / delFreq)
                                : (Number(variant.price) * priceAdj.value) / 100,
                });
            });
            let selectedDiscount = discountList.reduce((prev, current) =>
                prev.amount > current.amount ? prev : current
            );

            //handling for prepaid
            if (deliveryFreq > 1) {
                const variantFirstSellingPlan = variant.selling_plan_allocations.find(
                    (sp) => sp.selling_plan_group_id === spg.id
                );
                let ssp =
                    selling_plans.find(
                        (sp) =>
                            sp.id ===
                            Number(variantFirstSellingPlan.selling_plan_id)
                    ) || {};
                selectedDiscount = ssp.price_adjustments[0];
                deliveryFreq =
                    prepaidPlansData[ssp.id]?.deliveriesPerBillingCycle || 1;
            }

            let id = `#loop-discount-badge-${variant.id}-${spg.id}`;
            let element = getLoopSubscriptionContainer(productId).querySelector(
                id
            );

            if (
                window.loopProps[productId] &&
                spg.id === window.loopProps[productId].sellingPlanGroupId
            ) {
                let ssp =
                    selling_plans.find(
                        (sp) =>
                            sp.id ===
                            Number(window.loopProps[productId].sellingPlanId)
                    ) || {};
                selectedDiscount = ssp.price_adjustments[0];
                deliveryFreq =
                    prepaidPlansData[ssp.id]?.deliveriesPerBillingCycle || 1;
            }
            if (element) {
                let discountText = "";
                if (
                    selectedDiscount &&
                    selectedDiscount.value_type === "fixed_amount"
                ) {
                    discountText = loopFormatMoney(
                        selectedDiscount.value,
                        true
                    );
                } else if (
                    selectedDiscount &&
                    selectedDiscount.value_type === "percentage"
                ) {
                    discountText = `${selectedDiscount.value}%`;
                } else if (
                    selectedDiscount &&
                    selectedDiscount.value_type === "price"
                ) {
                    if (deliveryFreq > 1) {
                        const dic =
                            ((Number(variant.price) -
                                Number(selectedDiscount.value / deliveryFreq)) /
                                Number(variant.price)) *
                            100;
                        discountText = `${Math.round(dic > 0 ? dic : 0)}%`;
                    } else {
                        discountText = loopFormatMoney(
                            Number(variant.price) -
                                Number(selectedDiscount.value / deliveryFreq) >
                                0
                                ? Number(variant.price) -
                                Number(
                                    selectedDiscount.value / deliveryFreq
                                )
                                : 0,
                            true
                        );
                    }
                }

                let text = window?.loopPropsUI?.discountBadgeText || " ";
                let matchText = "{{discount_value}}";
                let discountLabelText = text.replace(
                    `{discount_value}`,
                    discountText
                );
                element.innerHTML = `${discountLabelText}`;
                if (
                    (!selectedDiscount?.value &&
                        !Number(selectedDiscount?.value)) ||
                    (selectedDiscount &&
                        selectedDiscount.value_type === "price" &&
                        Math.round(
                            Number(variant.price) -
                            Number(selectedDiscount.value / deliveryFreq)
                        ) <= 0)
                ) {
                    element.classList.add("loop-display-none");
                } else {
                    element.classList.remove("loop-display-none");
                }
            }
        });
    } else {
        selling_plan_groups.map((spg) => {
            let id = `#loop-discount-badge-${variant.id}-${spg.id}`;
            let element = document.querySelector(id);
            if (element) {
                element.classList.add("loop-display-none");
            }
        });
    }
}

/**
 * returns the current selling plan
 * @param {*} param0
 * @returns
 */
function calculateCurrentSellingPlanLoop({
    productId,
    availableSellingPlanAllocations,
}) {
    const loopProductProps = window.loopProps[productId];
    const { sellingPlanId, sellingPlanGroupId } = loopProductProps;

    return (
        availableSellingPlanAllocations.find((sellingPlan) => {
            if (sellingPlan.selling_plan_group_id !== sellingPlanGroupId) {
                return false;
            }
            if (sellingPlanId) {
                return (
                    Number(sellingPlan.selling_plan_id) ===
                    Number(sellingPlanId)
                );
            }
            return true;
        }) || {}
    );
}

function updateLoopProperties({
    product,
    productId,
    variantId,
    sellingPlanGroupId,
    sellingPlanGroupName,
    sellingPlanId,
}) {
    let loopProperties = getLoopSubscriptionContainer(productId).querySelector(
        "#loop-selling-plan-fieldset"
    );
    if (variantId) {
        if (
            Number(variantId) !==
            Number(loopProperties.dataset.selectedVariantId)
        ) {
            loopProperties.dataset.sellingPlanGroupId = "";
            loopProperties.dataset.sellingPlanGroupName = "";
            loopProperties.dataset.sellingPlanId = "";
        }
        loopProperties.dataset.selectedVariantId = variantId;
    }

    if (sellingPlanGroupId) {
        loopProperties.dataset.sellingPlanGroupId = sellingPlanGroupId;
    }
    if (sellingPlanGroupName) {
        loopProperties.dataset.sellingPlanGroupName = sellingPlanGroupName;
    }

    if (product) {
        loopProperties.dataset.product = JSON.stringify(product);
    }

    if (sellingPlanId) {
        loopProperties.dataset.sellingPlanId = sellingPlanId;
    } else if (sellingPlanGroupId === "loop-one-time-purchase") {
        loopProperties.dataset.sellingPlanId = "";
        loopProperties.dataset.sellingPlan = {};
        loopProperties.dataset.sellingPlan = {};
    }
    if (!window.loopProps) {
        window.loopProps = {};
        window.loopProps[productId] = { product, productId };
    }

    const productBundleData = window.loopProps[productId]["productBundleData"];
    const previousSPGId = window.loopProps[productId]["previousSPGId"];
    const bundleSPGS = window.loopProps[productId]["bundleSPGS"];
    const storefrontExcludedSPGS =
        window.loopProps[productId]["storefrontExcludedSPGS"];
    const nonBundleSPGS = window.loopProps[productId]["nonBundleSPGS"];
    const prepaidSellingPlans =
        window.loopProps[productId]["prepaidSellingPlans"];
    const productData = window.loopProps[productId]["product"];
    const storeJson = window.loopProps[productId]["storeJson"];
    const variantToSellingPlanGroups =
        window.loopProps[productId]["variantToSellingPlanGroups"];
    const variantToSellingPlans =
        window.loopProps[productId]["variantToSellingPlans"];
    const sellingPlanGroupToSellingPlans =
        window.loopProps[productId]["sellingPlanGroupToSellingPlans"];

    window.loopProps[productId] = { ...loopProperties.dataset, productId };

    window.loopProps[productId]["productBundleData"] = productBundleData;
    window.loopProps[productId]["previousSPGId"] = previousSPGId;
    window.loopProps[productId]["prepaidSellingPlans"] = prepaidSellingPlans;
    window.loopProps[productId]["product"] = productData;
    window.loopProps[productId]["bundleSPGS"] = bundleSPGS;
    window.loopProps[productId][
        "storefrontExcludedSPGS"
    ] = storefrontExcludedSPGS;
    window.loopProps[productId]["nonBundleSPGS"] = nonBundleSPGS;
    window.loopProps[productId]["storeJson"] = storeJson;
    window.loopProps[productId][
        "variantToSellingPlanGroups"
    ] = variantToSellingPlanGroups;
    window.loopProps[productId][
        "variantToSellingPlans"
    ] = variantToSellingPlans;
    window.loopProps[productId][
        "sellingPlanGroupToSellingPlans"
    ] = sellingPlanGroupToSellingPlans;

    let variant = findSelectedVariantLoop(productId);
    let availableSellingPlanAllocations =
        variant && Array.isArray(variant.selling_plan_allocations)
            ? variant.selling_plan_allocations
            : [];
    window.loopProps[
        productId
    ].availableSellingPlanAllocations = availableSellingPlanAllocations;
    window.loopProps[productId].variant = variant;

    let sellingPlan = calculateCurrentSellingPlanLoop({
        availableSellingPlanAllocations,
        productId,
    });
    let selectedSellingPlanId = sellingPlan.selling_plan_id || "";
    window.loopProps[productId].sellingPlan = sellingPlan;

    let sellingPlanAllocation = availableSellingPlanAllocations.find((aspa) => {
        if (selectedSellingPlanId) {
            if (
                Number(aspa.selling_plan_id) === Number(selectedSellingPlanId)
            ) {
                return true;
            }
        }
    });
    window.loopProps[productId].sellingPlanAllocation = sellingPlanAllocation;

    const { selling_plan_groups } = window.loopProps[productId].product;
    window.loopProps[productId].sellingPlanDefination = {};
    window.loopProps[productId].sellingPlanPriceAdjustments = [];
    if (selling_plan_groups && Array.isArray(selling_plan_groups)) {
        selling_plan_groups.map((spg) => {
            if (spg.id === window.loopProps[productId].sellingPlanGroupId) {
                const { selling_plans } = spg;
                selling_plans.map((sp) => {
                    if (
                        sp.id ===
                        Number(window.loopProps[productId].sellingPlanId)
                    ) {
                        window.loopProps[productId].sellingPlanDefination = sp;
                        window.loopProps[
                            productId
                        ].sellingPlanPriceAdjustments = sp.price_adjustments;
                    }
                });
            }
        });
    }

    let sellingPlanRadio = getLoopSubscriptionContainer(
        productId
    ).querySelector('[name="selling_plan"]');
    if (sellingPlanRadio) {
        sellingPlanRadio.value = selectedSellingPlanId;
    }
    //insert selling plan value in all the product from whose id is productId
    document
        .querySelectorAll(`form[data-loop-product-id="${productId}"]`)
        .forEach((form) => {
            const existingInputs = form.querySelectorAll(
                'input[name="selling_plan"]'
            );
            if (existingInputs.length) {
                existingInputs.forEach((existingInput) => {
                    existingInput.remove();
                });
            }

            const hiddenInput = document.createElement("input");
            hiddenInput.type = "hidden";
            hiddenInput.name = "selling_plan";
            hiddenInput.value = selectedSellingPlanId;
            form.appendChild(hiddenInput);

            // KC CUSTOM: inject/update subscription name as a cart attribute
            const existingSubAttrs = form.querySelectorAll(
                'input[name="attributes[Subscription Name]"], input[name="attributes[Subscription]"]'
            );
            existingSubAttrs.forEach((el) => el.remove());

            // Loop Loyalty awards Care Credit by this exact value ("60-Day Supply", "Year Supply"):
            // from the selling plan ID (assets/kc-plan-select.js), never from the visible plan name
            const subTitle = selectedSellingPlanId && window.KCPlanSelect
                ? window.KCPlanSelect.subscriptionName(selectedSellingPlanId)
                : "";

            if (subTitle) {
                const subNameInput = document.createElement("input");
                subNameInput.type = "hidden";
                subNameInput.name = "attributes[Subscription Name]";
                subNameInput.value = subTitle;
                form.appendChild(subNameInput);
            }
        });
}

function updateCartButtonTextLoop({ productId }) {
    const parentElement =
        document.querySelector(`[data-loop-product-id="${productId}"]`) ||
        document;
    const isOneTimeOrder = determineOneTimeOrderLoop(productId);
    const addToCartButton = getAddToCartButtonLoop(parentElement);
    if (!addToCartButton) return;

    const buttonText = getButtonTextLoop(isOneTimeOrder, productId);
    updateButtonInnerHTMLLoop(addToCartButton, buttonText);
}

function determineOneTimeOrderLoop(productId) {
    const sellingPlanGroupId = window?.loopProps[productId]?.sellingPlanGroupId;
    return (
        !sellingPlanGroupId || sellingPlanGroupId === "loop-one-time-purchase"
    );
}

function getAddToCartButtonLoop(parentElement) {
    const selectors = [
        "button[type='submit'][name='add']",
        "button[type='button'][name='add']",
    ];
    // return selectors.find((selector) => parentElement.querySelector(selector));
    let addToCartBtn = null;
    selectors.map((selector) => {
        if (!addToCartBtn) {
            addToCartBtn = parentElement.querySelector(selector);
        }
        if (!addToCartBtn) {
            addToCartBtn = document.querySelector(selector);
        }
    });

    return addToCartBtn;
}

function getButtonTextLoop(isOneTimeOrder, productId) {
    if (!window.loopProps[productId]["variant"]["available"]) {
        return (
            window?.loopPropsUI?.translationData?.widget_out_of_stock_text ||
            "Out of Stock"
        );
    } else if (!isOneTimeOrder) {
        return (
            window?.loopPropsUI?.translationData
                ?.widget_add_to_cart_button_for_subscription ||
            "Add subscription to cart"
        );
    } else {
        return (
            window?.loopPropsUI?.translationData
                ?.widget_add_to_cart_button_for_one_time_purchase ||
            "Add to cart"
        );
    }
}

function updateButtonInnerHTMLLoop(button, text) {
    if (button.firstElementChild) {
        button.firstElementChild.innerHTML = text;
    } else {
        button.innerHTML = text;
    }
}

/**
 * formats value based on money/money_without_currency filter of shopify
 * @param {*} price
 * @param {*} removeEach
 * @returns
 */
function loopFormatMoney(price, removeEach) {
    if (Shopify.locale && Shopify.country && Shopify.currency.active) {
        return loopDropZeroCents(loopFormatPriceNew(
            price,
            Shopify.locale,
            Shopify.country,
            Shopify.currency.active
        ), price);
    }

    const moneyFormat = document.querySelector("#loop-price-money-format")
        .innerText;
    const moneyWithoutCurrency = document.querySelector(
        "#loop-price-money_without_currency-format"
    ).innerText;

    let formattedPrice = loopFormatPrice(
        price,
        moneyFormat,
        moneyWithoutCurrency
    );

    if (removeEach) {
        formattedPrice = formattedPrice.replace("each", "");
    }
    return loopDropZeroCents(formattedPrice.trim(), price);
}

// Whole amounts drop their ".00" / ",00" (€59.00 -> €59, €0.49 stays €0.49),
// as in the Figma buybox.
function loopDropZeroCents(formatted, cents) {
    return Number(cents) % 100 === 0 ? formatted.replace(/[.,]00(?!\d)/, '') : formatted;
}

function loopFormatPriceNew(value, locale, countryCode, currencyCode) {
    const decimalValue = value / 100;
    const formatter = new Intl.NumberFormat(`${locale}-${countryCode}`, {
        style: "currency",
        currency: `${currencyCode}`,
    });
    return formatter.format(decimalValue);
}

function loopFormatPrice(price, moneyFormat, moneyWithoutCurrency) {
    const priceValue = price / 100;

    if (moneyFormat.includes("0.00")) {
        return moneyFormat.replace("0.00", priceValue.toFixed(2));
    } else if (moneyFormat.includes("0,00")) {
        return moneyFormat.replace(
            "0,00",
            priceValue.toFixed(2).replace(".", ",")
        );
    } else if (moneyFormat.includes("0")) {
        const wholeNumberValue = Number(
            moneyWithoutCurrency.replace("0", priceValue)
        ).toFixed(0);
        return moneyFormat.replace("0", wholeNumberValue);
    }
    return moneyFormat;
}

/**
 * saved price label in percentage/fixed value
 * @param {} priceAdjustments
 * @returns
 */
function getSavedPriceLabel(priceAdjustments) {
    if (!Array.isArray(priceAdjustments) || !priceAdjustments.length) {
        return "";
    }

    const pa = priceAdjustments[0];
    if (pa.value_type === "percentage") {
        return `Save ${pa.value}%`;
    } else {
        return `Save ${loopFormatMoney(pa.value, true)}`;
    }
}

/**
 * hide/show of selling plan description
 * @param {*} param0
 * @returns
 */
function updateLoopSellingPlanDescriptionUI({ productId }) {
    const variant = findSelectedVariantLoop(productId);
    const loopPropsProduct = window.loopProps?.[productId];

    if (!loopPropsProduct?.product?.selling_plan_groups) {
        return;
    }

    // Populate descriptions for ALL selling plan groups so that tooltips are available even when unchecked
    loopPropsProduct.product.selling_plan_groups.forEach(spg => {
        const firstSp = spg.selling_plans[0];
        const descriptionValue = firstSp?.description || "";
        const descriptionElement = document.querySelector(
            `#loop-selling-plan-description-${variant.id}-${spg.id}`
        );

        if (descriptionElement) {
            updateLoopSellingPlanDescriptionElement(
                descriptionElement,
                descriptionValue,
                productId
            );
        }
    });
    renderKCTestimonial(productId);

    if (typeof updateWelcomeKitUI === "function") {
        updateWelcomeKitUI(productId);
    }

    // Update dynamic Return-on-Care price spans after description HTML is refreshed
    if (typeof updateDynamicReturnOnCareLabels === "function") {
        updateDynamicReturnOnCareLabels(productId);
    }
}

// ── Welcome-kit product popup ──────────────────────────────────────────
function loopShowWelcomeKitProductPopup(title, image, price, comparePrice) {
    // Styles are loaded via loop-widget.css (linked in theme.liquid)

    // Build overlay
    const overlay = document.createElement("div");
    overlay.className = "loop-wk-overlay";
    overlay.innerHTML = `
        <div class="loop-wk-modal" role="dialog" aria-modal="true" aria-label="${title}">
            <button class="loop-wk-close" aria-label="Close">&#x2715;</button>
            <img class="loop-wk-modal-img" src="${image}" alt="${title}" />
            <div class="loop-wk-modal-info">
                <p class="loop-wk-modal-title">${title}</p>
                <p class="loop-wk-modal-price">
                    <span class="loop-wk-free">Free</span> · <del>${comparePrice}</del>
                </p>
                <p class="loop-wk-modal-hint">Click next to the image to close</p>
            </div>
        </div>
    `;

    const closePopup = () => {
        overlay.classList.remove("loop-wk-visible");
        setTimeout(() => overlay.remove(), 280);
        document.removeEventListener("keydown", onKey);
    };
    const onKey = (e) => { if (e.key === "Escape") closePopup(); };

    // Close on any click next to the image (backdrop, name, price); a click
    // on the image itself keeps it open
    overlay.addEventListener("click", (e) => {
        if (!e.target.closest(".loop-wk-modal-img")) closePopup();
    });
    overlay.querySelector(".loop-wk-close").addEventListener("click", closePopup);
    document.addEventListener("keydown", onKey);

    document.body.appendChild(overlay);
    // Trigger animation on next frame
    requestAnimationFrame(() => requestAnimationFrame(() => overlay.classList.add("loop-wk-visible")));
}
// ── End popup helper ─────────────────────────────────────────────────────

// KC: by selling plan ID, never by plan name. The Welcome Kit comes free with every subscription
// (karmi-add-to-cart.js adds it to each one), so a blank "Welcome Kit plan IDs" block setting
// (data-plans) qualifies every subscription plan; a list of selling plan IDs limits it to those.
function checkPlanQualifiesForWelcomeKit(productId) {
    const sellingPlanId = String(window.loopProps?.[productId]?.sellingPlanId || "");
    if (!sellingPlanId || determineOneTimeOrderLoop(productId)) return false;

    const dataEl = document.querySelector("#loop-welcome-kit-data");
    if (!dataEl) return false;

    const entries = (dataEl.dataset.plans || "").split(",").map(p => p.trim()).filter(Boolean);
    const planIds = entries.filter(p => /^\d+$/.test(p));
    if (planIds.length !== entries.length && !window.kcWelcomeKitPlansWarned) {
        window.kcWelcomeKitPlansWarned = true;
        console.warn("[KC plans] Welcome Kit plan IDs: ignoring entries that are not selling plan IDs", entries);
    }
    return planIds.length === 0 || planIds.includes(sellingPlanId);
}

function updateWelcomeKitUI(productId) {
    const variant = findSelectedVariantLoop(productId);
    const loopPropsProduct = window.loopProps?.[productId];
    if (!loopPropsProduct || !variant) return;

    const descriptionElement = document.querySelector(
        `#loop-selling-plan-description-${variant.id}-${loopPropsProduct.sellingPlanGroupId}`
    );
    if (!descriptionElement) return;

    // Prevent flickering by checking if the welcome kit is already inside this exact description element
    const existingWelcomeKit = descriptionElement.querySelector(".loop-welcome-kit-section");
    if (existingWelcomeKit) {
        return; // Already present, no need to recreate
    }

    // Remove from other places (like inactive plans)
    const existingSections = document.querySelectorAll(".loop-welcome-kit-section");
    if (existingSections) {
        existingSections.forEach(section => section.remove());
    }

    const qualifies = checkPlanQualifiesForWelcomeKit(productId);
    if (!qualifies) return;

    const dataEl = document.querySelector("#loop-welcome-kit-data");
    if (!dataEl) return;

    const title = dataEl.dataset.title || "Welcome Kit · with first order";
    const productItems = dataEl.querySelectorAll(".loop-welcome-kit-product-item");
    if (productItems.length === 0) return;

    let totalValue = 0;
    let productsHtml = "";

    productItems.forEach(item => {
        const itemTitle = item.dataset.title || "";
        const itemImage = item.dataset.image || "";
        // Popup shows the product's main image; the tile uses the close-up thumbnail
        const itemFullImage = item.dataset.fullImage || itemImage;
        const itemPrice = parseInt(item.dataset.price || "0");
        const itemComparePrice = parseInt(item.dataset.comparePrice || item.dataset.price || "0");
        const itemUrl = item.dataset.url || "#";

        totalValue += itemComparePrice;

        const formattedComparePrice = loopFormatMoney(itemComparePrice, true);

        productsHtml += `
            <div class="loop-welcome-kit-product loop-wk-clickable"
                 style="cursor:pointer;"
                 data-wk-title="${itemTitle}"
                 data-wk-image="${itemImage}"
                 data-wk-full-image="${itemFullImage}"
                 data-wk-price="${formattedComparePrice}"
                 data-wk-compare-price="${formattedComparePrice}">
                <img class="loop-welcome-kit-product-img" src="${itemImage}" alt="${itemTitle}" loading="lazy" />
                <span class="loop-welcome-kit-product-link" title="${itemTitle}">${itemTitle}</span>
                <div class="loop-welcome-kit-product-price opacity-xl">
                    <span>Free</span> · <del>${formattedComparePrice}</del>
                </div>
            </div>
        `;
    });

    const formattedTotalValue = loopFormatMoney(totalValue, true);

    const welcomeKitContainer = document.createElement("div");
    welcomeKitContainer.className = "loop-welcome-kit-section";

    // KC: open for the subscription selected in the buybox (the kit sits in that plan's description),
    // unless the shopper closed it; no longer tied to the first plan radio on the page.
    if (!window.loopWelcomeKitExpanded) {
        window.loopWelcomeKitExpanded = {};
    }
    const isCurrentlyExpanded = window.loopWelcomeKitExpanded[productId] !== false;
    if (isCurrentlyExpanded) {
        welcomeKitContainer.classList.add("is-expanded");
    }

    welcomeKitContainer.innerHTML = `
        <div class="loop-welcome-kit-header">
            <span class="loop-welcome-kit-title">${title}</span>
            <div style="display: flex; align-items: center;">
                <span class="loop-welcome-kit-badge">${formattedTotalValue} value · free</span>
                <span class="loop-welcome-kit-toggle"></span>
            </div>
        </div>
        <div class="loop-welcome-kit-products">
            ${productsHtml}
        </div>
    `;

    // Attach event listener for collapsing/expanding
    const header = welcomeKitContainer.querySelector(".loop-welcome-kit-header");
    header.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const expanded = welcomeKitContainer.classList.toggle("is-expanded");
        window.loopWelcomeKitExpanded[productId] = expanded;
    });

    // Attach click listeners to each product card to open popup
    const productCards = welcomeKitContainer.querySelectorAll(".loop-wk-clickable");
    productCards.forEach(card => {
        card.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            const t = card.dataset.wkTitle;
            const img = card.dataset.wkImage;
            const cp = card.dataset.wkComparePrice;
            // Main product image for the popup; older markup without it falls
            // back to the tile image at a larger width
            const largeImg = card.dataset.wkFullImage || img.replace(/width=\d+/, "width=600");
            loopShowWelcomeKitProductPopup(t, largeImg, cp, cp);
        });
    });

    // (Variables variant, loopPropsProduct, and descriptionElement are already declared at the top of the function)

    if (descriptionElement) {
        descriptionElement.classList.remove("loop-display-none");
        const targetContainer = descriptionElement.querySelector("div") || descriptionElement;

        // Try to find the specific placeholder class in the description
        const welcomeKitTarget = targetContainer.querySelector(".block__show-welcome-kit");
        if (welcomeKitTarget) {
            welcomeKitTarget.appendChild(welcomeKitContainer);
        } else {
            // Fallback: Find the first checklist/bullet point item (direct child containing "✓") to insert BEFORE it
            let checklistElement = null;
            const children = targetContainer.children;
            for (const child of children) {
                if (child.textContent.includes("✓")) {
                    checklistElement = child;
                    break;
                }
            }

            if (checklistElement) {
                checklistElement.parentNode.insertBefore(welcomeKitContainer, checklistElement);
            } else {
                targetContainer.appendChild(welcomeKitContainer);
            }
        }
    }

    // Update all .dynamic_free_welcome_price spans in the active description
    // so checklist lines like "Free Welcome Kit (€17.00 in value)" stay in sync
    if (descriptionElement) {
        descriptionElement.querySelectorAll('.dynamic_free_welcome_price').forEach(el => {
            el.textContent = formattedTotalValue;
        });
    }
}

function translateDescriptionEuroPrices(html) {
    if (!html) return html;
    try {
        const rate = parseFloat(window.Shopify?.currency?.rate || 1.0);
        const regex = /(?:€|&euro;)\s*([0-9]+(?:\.[0-9]+)?)/g;

        return html.replace(regex, (match, p1) => {
            const eurVal = parseFloat(p1);
            const activeVal = eurVal * rate;
            const cents = Math.round(activeVal * 100);
            let formatted = loopFormatMoney(cents, true);

            if (!p1.includes('.')) {
                formatted = formatted.replace(/[\.,]00$/, '');
            }
            return formatted;
        });
    } catch (e) {
        console.error("Error translating description prices: ", e);
        return html;
    }
}

function updateLoopSellingPlanDescriptionElement(
    descriptionElement,
    descriptionValue
) {
    if (!descriptionElement) return;

    const translatedDescriptionValue = translateDescriptionEuroPrices(descriptionValue);

    // Calculate a simple hash or use the raw string to prevent resetting innerHTML if it hasn't changed.
    // This prevents flickering caused by constant DOM destruction and recreation.
    const currentBaseContent = translatedDescriptionValue || "";

    if (descriptionElement.dataset.baseContent === currentBaseContent) {
        if (!descriptionValue) {
            descriptionElement.classList.add("loop-display-none");
        } else {
            descriptionElement.classList.remove("loop-display-none");
        }
        return;
    }

    descriptionElement.dataset.baseContent = currentBaseContent;
    descriptionElement.innerHTML = translatedDescriptionValue;

    if (!descriptionValue) {
        descriptionElement.classList.add("loop-display-none");
    } else {
        descriptionElement.classList.remove("loop-display-none");
    }
}

function updatePriceInParentElementsLoop({ productId }) {
    const currentPath = getCurrentPathLoop();
    const productHandle = window?.loopProps[productId]?.product?.handle;

    if (productHandle !== currentPath) {
        return;
    }

    const variant = findSelectedVariantLoop(productId);
    const price = determinePriceLoop(productId, variant);

    loopPriceSelectors.push(`.loop-product-${productId}`);
    updatePricesInUILoop(price);

    // Update and toggle the kc-pricing-summary block
    updateKcPricingSummary(productId, variant);

    // Update per-brushing sub-row on each subscription plan card
    updateSubscriptionGroupCardSubRow(productId, variant);

    // Inject and update the "Or Try once" link above the pricing summary
    injectOrUpdateKcTryOnceLink(productId, variant);
}

/* ============================================================
   KC CUSTOM FUNCTIONS
   - kcPlanPrice                        : what the checkout charges for a plan
   - updateSubscriptionGroupCardSubRow  : Plan card sub-row
   - updateKcPricingSummary             : "Total today" summary box
   ============================================================ */

/**
 * What the checkout charges for a selling plan allocation, in cents: Shopify's price per quantity (the
 * allocation price) x quantity, the quantity being the plan's Duos per delivery (assets/kc-plan-select.js;
 * year plans follow the theme setting "Year plans: Duos per delivery", everything else is 1). For a year
 * plan that is Shopify's year price per quantity x quantity (€118 x 3 = €354): a year price, never a
 * per-Duo price. Every price shown for a plan reads this, never the price per quantity alone, so the
 * page shows what the cart line costs.
 */
function kcPlanPrice(allocation) {
    if (!allocation || allocation.price == null) return allocation?.price;
    return Number(allocation.price) * kcPlanUnits(allocation.selling_plan_id);
}

// The Duos per delivery of a selling plan (assets/kc-plan-select.js), 1 without it
function kcPlanUnits(sellingPlanId) {
    return window.KCPlanSelect && sellingPlanId ? window.KCPlanSelect.units(sellingPlanId) : 1;
}

/**
 * The allocation a plan card stands for: the plan selected in the card's own select when the variant
 * offers it and the page may sell it, else the group's first such allocation. Every price-derived value
 * of the card (price, per brushing, Care Credit) reads this one, never a private first plan.
 */
function kcCardAllocation(productId, variant, groupId) {
    const allocations = (variant?.selling_plan_allocations || []).filter(
        (a) => String(a.selling_plan_group_id) === String(groupId)
    );
    const allowed = (a) => !window.KCPlanSelect || window.KCPlanSelect.allowed(a.selling_plan_id);
    const container = getLoopSubscriptionContainer(productId);
    const select = container && container.querySelector(`#loop-select-${variant.id}-${groupId}`);
    const chosen = select && allocations.find((a) => String(a.selling_plan_id) === String(select.value));
    if (chosen && allowed(chosen)) return chosen;
    return allocations.find(allowed) || allocations[0];
}

/**
 * Plan card sub-row (dynamic, per variant selection)
 * LEFT  (.loop-subscription-group-text):          "£0.43 / brushing · ships every 60 days"
 * RIGHT (.loop-subscription-group-price-container): "/ 60 days"
 */
function updateSubscriptionGroupCardSubRow(productId, variant) {
    if (!variant?.selling_plan_allocations?.length) return;
    const loopContainer = getLoopSubscriptionContainer(productId);
    if (!loopContainer) return;

    const dayMap = { day: 1, week: 7, month: 30, year: 365 };

    loopContainer.querySelectorAll('.loop-subscription-group').forEach((groupEl) => {
        const radio = groupEl.querySelector('.loop-subscription-group-radio');
        if (!radio?.dataset?.id || radio.dataset.id === 'loop-one-time-purchase') return;

        // KC: the card's selected, offered, allowed plan, not the group's first one
        const alloc = kcCardAllocation(productId, variant, radio.dataset.id);
        if (!alloc?.price) return;

        // 1. Shipping label — read directly from Loop's <select> option (e.g. "ships every 60 days")
        const select = groupEl.querySelector('.loop-selling-plan-selector');
        const optionText = (select?.options?.[select.selectedIndex]?.text || select?.querySelector('option')?.text || '').trim();

        // 2. Billing interval for per-brushing calculation and right-side short label, by selling plan
        // ID (assets/kc-plan-select.js), never from the plan name
        const kcPlan = window.KCPlanSelect ? window.KCPlanSelect.plan(alloc.selling_plan_id) : null;
        const bp = alloc?.selling_plan?.billing_policy;
        let bInterval = (bp?.interval || kcPlan?.intervalUnit || '').toLowerCase();
        let bCount = Number(bp?.interval_count) || kcPlan?.intervalCount || 1;
        if (!bInterval) return;

        const billingDays = (dayMap[bInterval] || 1) * bCount;
        const isAnnual = bInterval === 'year' || (bInterval === 'month' && bCount === 12);

        // Determine duos for calculation
        const option = select?.querySelector(`option[value="${alloc.selling_plan_id}"]`);
        const duosCount = parseInt(option?.dataset?.duos) || 1;
        const brushingsPerDuo = parseInt(option?.dataset?.brushings) || 120;

        // 3. Per-brushing cost (what the checkout charges ÷ (duos * 120))
        let totalBrushings = duosCount * brushingsPerDuo;
        const perBrushingCents = Math.round(kcPlanPrice(alloc) / totalBrushings);
        const perBrushingFormatted = loopFormatMoney(perBrushingCents, true);

        // 4. Right-side short label: "/ year" or "/ 60 days"
        const rightLabel = isAnnual ? '/ year' : `/ ${billingDays} days`;

        // LEFT: inject/update .kc-plan-subrow inside .loop-subscription-group-text (below plan name)
        const textContainer = groupEl.querySelector('.loop-subscription-group-text');
        if (textContainer) {
            let subRow = textContainer.querySelector('.kc-plan-subrow');
            if (!subRow) {
                subRow = document.createElement('div');
                subRow.className = 'kc-plan-subrow';
                textContainer.appendChild(subRow);
            }
            let formattedOptionText = optionText.replace(/ships/i, 'Renews');
            const perBrushingLine = `${perBrushingFormatted} / brushing`;
            if (isAnnual) {
                // Figma: "Renews every year" / "€0.49 / brushing" (Victor 16 Oct: without the
                // Figma "· 6 duos in two deliveries")
                formattedOptionText = 'Renews every year';
            }
            subRow.innerHTML = formattedOptionText
                ? `<div>${formattedOptionText}</div> <div>${perBrushingLine}</div>`
                : `<div>${perBrushingLine}</div>`;
        }

        // RIGHT: inject/update .kc-plan-interval-label inside .loop-subscription-group-price-container
        const priceContainer = groupEl.querySelector('.loop-subscription-group-price-container');
        if (priceContainer) {
            let labelEl = priceContainer.querySelector('.kc-plan-interval-label');
            if (!labelEl) {
                labelEl = document.createElement('span');
                labelEl.className = 'kc-plan-interval-label';
                priceContainer.appendChild(labelEl);
            }
            labelEl.textContent = rightLabel;
        }
    });
}

/**
 * Updates kc-dynamic_price elements inside each
 * plan card description with the correct calculated Return-on-Care amount.
 */

function updateDynamicReturnOnCareLabels(productId) {
    const variant = findSelectedVariantLoop(
        productId,
        window.loopProps?.[productId]?.selectedVariantId
    );
    if (!variant?.selling_plan_allocations?.length) return;

    const loopContainer = getLoopSubscriptionContainer(productId);
    if (!loopContainer) return;

    loopContainer.querySelectorAll('.loop-subscription-group').forEach((groupEl) => {
        const radio = groupEl.querySelector('.loop-subscription-group-radio');
        if (!radio?.dataset?.id || radio.dataset.id === 'loop-one-time-purchase') return;

        // KC: the card's selected, offered, allowed plan, not the group's first one
        const alloc = kcCardAllocation(productId, variant, radio.dataset.id);
        if (!alloc?.price) return;

        const planPrice = kcPlanPrice(alloc); // what the checkout charges, in cents

        // Fetch return-on-care percentage from the currently selected option
        const selectId = `loop-select-${variant.id}-${radio.dataset.id}`;
        const selectEl = loopContainer.querySelector(`#${selectId}`);
        const selectedOption = selectEl?.querySelector(`option[value="${alloc.selling_plan_id}"]`);

        let returnOnCareVal = 0;
        if (selectedOption?.dataset?.returnOnCare) {
            returnOnCareVal = parseInt(selectedOption.dataset.returnOnCare) || 0;
        }

        const pct = returnOnCareVal / 100;

        // Display the calculated discount money amount wherever kc-dynamic_price is used
        groupEl.querySelectorAll('.kc-dynamic_price').forEach(el => {
            const amount = Math.round(planPrice * pct);
            const formatted = loopFormatMoney(amount, true);
            if (el.textContent !== formatted) el.textContent = formatted;
        });

        // Display the percentage number (e.g. 20%) wherever kc-dynamic_percent is used
        groupEl.querySelectorAll('.kc-dynamic_percent').forEach(el => {
            const perc = `${returnOnCareVal}%`;
            if (el.textContent !== perc) el.textContent = perc;
        });
    });
}

/**
 * "Total today" summary pricing box (shown on subscription, hidden on one-time)
 */
function updateKcPricingSummary(productId, variant) {
    const isOneTime = determineOneTimeOrderLoop(productId);

    // Find the summary block - it uses section id embedded in the id attribute
    const summaryEl = document.querySelector('.kc-pricing-summary[id^="kc-pricing-summary-"]');
    const priceBlock = document.querySelector('[id^="price-"]');
    const taxBlock = document.querySelector('.product__tax');
    const installmentBlock = document.querySelector('.installment');

    if (!summaryEl) return;

    let tryOnceContainer = document.querySelector(`#kc-try-once-container-${productId}`);

    if (isOneTime) {
        // Hide default price block
        if (priceBlock) priceBlock.style.display = 'none';
        if (taxBlock) taxBlock.style.display = 'none';
        if (installmentBlock) installmentBlock.style.display = 'none';

        if (tryOnceContainer) tryOnceContainer.style.display = 'block';
    } else {
        // Subscription: hide default price block, show summary, show try once link
        if (priceBlock) priceBlock.style.display = 'none';
        if (taxBlock) taxBlock.style.display = 'none';
        if (installmentBlock) installmentBlock.style.display = 'none';

        if (tryOnceContainer) tryOnceContainer.style.display = 'block';
    }

    const sellingPlanAllocation = window?.loopProps[productId]?.sellingPlanAllocation;
    // KC: what the checkout charges (Shopify's price per quantity x quantity): total, per brushing, Care Credit
    const sellingPlanPrice = kcPlanPrice(sellingPlanAllocation);
    if (!variant) return;
    if (!isOneTime && !sellingPlanPrice) return;
    // KC: the selected plan by selling plan ID (interval, Check-up Reward), never by its name
    const kcPlan = !isOneTime && window.KCPlanSelect
        ? window.KCPlanSelect.plan(window.loopProps[productId]?.sellingPlanId)
        : null;

    // Format total price
    let totalFormatted = '';
    if (!isOneTime && sellingPlanPrice) {
        totalFormatted = loopFormatMoney(sellingPlanPrice, true);
    }

    // KC: the receipt's texts in the page language: kc.receipt.* keys as data attributes on the
    // summary (snippets/kc-receipt-labels.liquid), [count] and [price] filled here. English where an
    // attribute is missing.
    const kcReceiptText = (key, english, values = {}) =>
        String((key && summaryEl.dataset[key]) || english)
            .replace(/\[(count|price)\]/g, (match, name) => (name in values ? values[name] : match));

    // Build billing interval label (e.g. "billed every 8 weeks", "billed yearly")
    let intervalLabel = '';
    let perBrushingLabel = '';
    try {
        // Source 1: sellingPlanAllocation.selling_plan.billing_policy (from variant JSON)
        // Source 2: sellingPlanDefination.billing_policy (from selling_plan_groups JSON)
        // Source 3: the plan by selling plan ID (assets/kc-plan-select.js), never its name
        const billingPolicyA = sellingPlanAllocation?.selling_plan?.billing_policy;
        const billingPolicyB = window.loopProps[productId]?.sellingPlanDefination?.billing_policy;
        const billingPolicy = billingPolicyA || billingPolicyB;

        let interval = '';
        let intervalCount = 1;

        if (billingPolicy) {
            interval = (billingPolicy.interval || '').toLowerCase();
            intervalCount = Number(billingPolicy.interval_count) || 1;
        } else if (kcPlan && kcPlan.intervalUnit) {
            interval = kcPlan.intervalUnit;
            intervalCount = kcPlan.intervalCount || 1;
        }

        if (interval) {
            const intervalMap = { day: 'day', week: 'week', month: 'month', year: 'year' };
            const base = intervalMap[interval] || interval;
            // KC: the attribute per unit and number, e.g. kcPerDays / kcBilledDays (kc.receipt.per_days)
            const kcUnit = intervalMap[interval] ? base.charAt(0).toUpperCase() + base.slice(1) + (intervalCount === 1 ? '' : 's') : '';

            // Interval label on right: "/ year", "/ 8 weeks", etc.
            if (intervalCount === 1) {
                intervalLabel = kcReceiptText(kcUnit && `kcPer${kcUnit}`, `/ ${base}`);
            } else {
                intervalLabel = kcReceiptText(kcUnit && `kcPer${kcUnit}`, `/ ${intervalCount} ${base}s`, { count: intervalCount });
            }

            // Per-brushing calculation (dynamically driven by data-duos and data-brushings)
            let duosCount = 1;
            let brushingsPerDuo = 120;
            const loopPropsProduct = window.loopProps?.[productId];
            if (loopPropsProduct && loopPropsProduct.sellingPlanGroupId && loopPropsProduct.sellingPlanId) {
                const loopContainer = getLoopSubscriptionContainer(productId);
                if (loopContainer) {
                    const select = loopContainer.querySelector(`#loop-select-${loopPropsProduct.selectedVariantId}-${loopPropsProduct.sellingPlanGroupId}`);
                    if (select) {
                        const option = select.querySelector(`option[value="${loopPropsProduct.sellingPlanId}"]`);
                        if (option) {
                            if (option.dataset.duos) duosCount = parseInt(option.dataset.duos) || 1;
                            if (option.dataset.brushings) brushingsPerDuo = parseInt(option.dataset.brushings) || 120;
                        }
                    }
                }
            }

            const totalBrushings = duosCount * brushingsPerDuo;

            if (totalBrushings > 0) {
                const perBrushingCents = Math.round(sellingPlanPrice / totalBrushings);
                const perBrushingFormatted = loopFormatMoney(perBrushingCents, true);

                let billedLabel = '';
                if (intervalCount === 1 && interval === 'year') {
                    billedLabel = 'Billed yearly';
                } else if (intervalCount === 1) {
                    billedLabel = `Billed every ${base}`;
                } else {
                    billedLabel = `Billed every ${intervalCount} ${base}s`;
                }
                // Figma receipt: "Billed every 60 days" (the per-brushing price is on the plan card)
                perBrushingLabel = kcReceiptText(kcUnit && `kcBilled${kcUnit}`, billedLabel, { count: intervalCount });
            }
        }
    } catch (e) {
        console.warn('kc-pricing-summary: could not compute per-brushing', e);
    }

    if (isOneTime) {
        // One-time styling overrides
        const oneTimePriceCents = variant.price;
        totalFormatted = loopFormatMoney(oneTimePriceCents, true);
        intervalLabel = kcReceiptText('kcOneTime', 'one-time');
        // Assume 120 brushings (60 days) for one-time standard size as fallback
        const perBrushingCents = Math.round(oneTimePriceCents / 120);
        perBrushingLabel = kcReceiptText('kcOneTimeNote', '[price] per brushing · no Care Credit', {
            price: loopFormatMoney(perBrushingCents, true),
        });
    }

    // Populate the summary element fields
    const priceEl = summaryEl.querySelector('.kc-pricing-summary__price');
    const perBrushingEl = summaryEl.querySelector('.kc-pricing-summary__per-brushing');
    const intervalEl = summaryEl.querySelector('.kc-pricing-summary__interval');

    if (priceEl) priceEl.textContent = totalFormatted;
    if (perBrushingEl) perBrushingEl.textContent = perBrushingLabel;
    if (intervalEl) intervalEl.textContent = intervalLabel;

    // --- Update Rewards Block ---
    let rewardsBlock = summaryEl.querySelector('.kc-pricing-summary__rewards');

    if (isOneTime) {
        if (rewardsBlock) rewardsBlock.style.display = 'none';
    } else {
        let careCreditCents = 0;
        let checkupRewardStr = '';

        const basePrice = sellingPlanPrice || variant.price;

        // Same source as the plan card chip: return_on_care % from the
        // custom.duos_count metaobject (data-return-on-care on the plan option).
        let returnOnCarePct = 0;
        const loopPropsForCredit = window.loopProps?.[productId];
        const creditContainer = getLoopSubscriptionContainer(productId);
        if (creditContainer && loopPropsForCredit?.sellingPlanGroupId && loopPropsForCredit?.sellingPlanId) {
            const creditOption = creditContainer.querySelector(
                `#loop-select-${loopPropsForCredit.selectedVariantId}-${loopPropsForCredit.sellingPlanGroupId} option[value="${loopPropsForCredit.sellingPlanId}"]`
            );
            returnOnCarePct = parseInt(creditOption?.dataset?.returnOnCare) || 0;
        }

        // Fallback when the metaobject has no entry for this plan: the plan's Care Credit % by selling
        // plan ID (snippets/kc-plan-type.liquid: 60-day 10, year 20)
        const creditPct = returnOnCarePct > 0 ? returnOnCarePct : (kcPlan?.careCreditPercent || 0);
        careCreditCents = Math.round(basePrice * creditPct / 100);
        if (kcPlan?.checkupReward) {
            // Same wording as the Year card chip "€40 Dental Check-up Reward", in the page
            // language's money format (€40, € 40, 40 €)
            checkupRewardStr = loopFormatMoney(4000, true);
        }

        if (rewardsBlock) {
            let hasRewards = false;

            const careCreditRow = rewardsBlock.querySelector('.kc-care-credit-row');
            if (careCreditRow) {
                if (careCreditCents > 0) {
                    const valueEl = careCreditRow.querySelector('.kc-care-credit-value');
                    if (valueEl) valueEl.textContent = loopFormatMoney(careCreditCents, true);
                    careCreditRow.style.display = 'flex';
                    hasRewards = true;
                } else {
                    careCreditRow.style.display = 'none';
                }
            }

            const checkupRow = rewardsBlock.querySelector('.kc-checkup-reward-row');
            if (checkupRow) {
                if (checkupRewardStr) {
                    const valueEl = checkupRow.querySelector('.kc-checkup-reward-value');
                    if (valueEl) valueEl.textContent = checkupRewardStr;
                    checkupRow.style.display = 'flex';
                    hasRewards = true;
                } else {
                    checkupRow.style.display = 'none';
                }
            }

            // "Welcome Kit on first order (value €21) … FREE", only for plans that get the kit
            const kitRow = rewardsBlock.querySelector('.kc-welcome-kit-row');
            if (kitRow) {
                const kitValueCents = getWelcomeKitValueCents();
                if (kitValueCents > 0 && checkPlanQualifiesForWelcomeKit(productId)) {
                    const labelEl = kitRow.querySelector('.kc-welcome-kit-label');
                    if (labelEl) {
                        const template = labelEl.dataset.template || labelEl.textContent;
                        labelEl.dataset.template = template;
                        labelEl.textContent = template.replace('[value]', loopFormatMoney(kitValueCents, true));
                    }
                    kitRow.style.display = 'flex';
                    hasRewards = true;
                } else {
                    kitRow.style.display = 'none';
                }
            }

            rewardsBlock.style.display = hasRewards ? 'block' : 'none';
        }
    }

    updateKcPlanButtonLabel(productId, isOneTime);
    summaryEl.style.display = 'block';
}

// Sum of the Welcome Kit items' values (compare-at, else price), in cents
function getWelcomeKitValueCents() {
    const dataEl = document.querySelector('#loop-welcome-kit-data');
    if (!dataEl) return 0;
    let total = 0;
    dataEl.querySelectorAll('.loop-welcome-kit-product-item').forEach((item) => {
        total += parseInt(item.dataset.comparePrice || item.dataset.price) || 0;
    });
    return total;
}

// CTA label per selected plan, by selling plan ID. The buy button carries data-plan-labels
// ({"712106115453": "Start my 60-Day rhythm", …}) from the buy_buttons block;
// plans without a label, and one-time, use the button's default text.
function updateKcPlanButtonLabel(productId, isOneTime) {
    const sellingPlanId = String(window.loopProps?.[productId]?.sellingPlanId || '').trim();
    document.querySelectorAll('.product-form__custom-submit[data-plan-labels]').forEach((button) => {
        const labelEl = button.querySelector('.kc-cta-label');
        if (!labelEl) return;
        let labels = {};
        try { labels = JSON.parse(button.dataset.planLabels || '{}'); } catch (e) { labels = {}; }
        const keys = Object.keys(labels).map((key) => key.trim());
        if (keys.some((key) => !/^\d+$/.test(key)) && !button.dataset.kcLabelsWarned) {
            button.dataset.kcLabelsWarned = 'true';
            console.warn('[KC plans] Custom Button Text per plan: lines must start with a selling plan ID', keys);
        }
        const match = !isOneTime && sellingPlanId && Object.keys(labels).find((key) => key.trim() === sellingPlanId);
        labelEl.textContent = match ? labels[match] : button.dataset.defaultLabel;
    });
}

function injectOrUpdateKcTryOnceLink(productId, variant) {
    if (!variant) return;

    const loopContainer = getLoopSubscriptionContainer(productId);
    if (loopContainer) {
        const topCard = loopContainer.querySelector('#loop-one-time-purchase-option-at-top');
        if (topCard) {
            topCard.style.setProperty('display', 'none', 'important');
            topCard.setAttribute('aria-hidden', 'true');
        }
    }

    const summaryEl = document.querySelector('.kc-pricing-summary[id^="kc-pricing-summary-"]');
    if (!summaryEl) return;

    let tryOnceContainer = document.querySelector(`#kc-try-once-container-${productId}`);
    if (!tryOnceContainer) return;

    const oneTimePriceFormatted = loopFormatMoney(variant.price, true);

    // Update the dynamic price span inside the button
    const priceSpan = tryOnceContainer.querySelector('.kc-try-once-price');
    if (priceSpan) {
        priceSpan.textContent = oneTimePriceFormatted;
    }

    const btn = tryOnceContainer.querySelector('.kc-try-once-btn');
    if (btn && !btn.dataset.bound) {
        btn.dataset.bound = 'true';
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            // One time, straight to checkout, from this product's buybox form (assets/kc-plan-select.js)
            if (window.KCPlanSelect) {
                window.KCPlanSelect.tryOnce(productId, btn);
            } else {
                console.warn('[KC plans] Try Once: kc-plan-select.js is not loaded');
            }
        });
    }
}

function determinePriceLoop(productId, variant) {
    // KC: what the checkout charges (Shopify's price per quantity x quantity)
    const sellingPlanPrice = kcPlanPrice(
        window?.loopProps[productId]?.sellingPlanAllocation
    );

    if (sellingPlanPrice) {
        return loopFormatMoney(sellingPlanPrice, true);
    }
    return loopFormatMoney(variant.price, true);
}

function updatePricesInUILoop(price) {
    loopPriceSelectors.forEach((selector) => {
        const priceElements = document.querySelectorAll(selector);
        priceElements.forEach((priceElement) => {
            priceElement.innerHTML = `${price}`;
        });
    });
}

function updatePriceInUILoop({ productId }) {
    let variant = findSelectedVariantLoop(
        productId,
        window.loopProps[productId].selectedVariantId
    );

    let sellingPlan =
        window.loopProps && window.loopProps[productId]
            ? window.loopProps[productId].sellingPlan
            : {};
    const product = window.loopProps[productId]?.product || {};
    const { selling_plan_groups } = product;
    const { selling_plan_allocations } = variant;
    selling_plan_groups.map((spg) => {
        if (Array.isArray(spg.selling_plans) && spg.selling_plans.length) {
            // KC: the card's selected, offered, allowed plan (kcCardAllocation), not the group's first one
            let sellingPlanAllcotion = kcCardAllocation(productId, variant, spg.id) || {};
            const { selling_plan_group_id } = sellingPlanAllcotion;
            // KC: what the checkout charges (Shopify's price per quantity x quantity)
            const price = kcPlanPrice(sellingPlanAllcotion);
            let element = document.querySelector(
                `#loop-price-${variant.id}-${selling_plan_group_id}`
            );
            if (element) {
                element.innerHTML = loopFormatMoney(price, true);
            }
        }
    });

    if (sellingPlan && sellingPlan.selling_plan_group_id) {
        const { selling_plan_group_id } = sellingPlan;
        // KC: what the checkout charges (Shopify's price per quantity x quantity)
        const price = kcPlanPrice(sellingPlan);
        let element = document.querySelector(
            `#loop-price-${variant.id}-${selling_plan_group_id}`
        );
        if (element) {
            element.innerHTML = loopFormatMoney(price, true);
        }
    }

    let loopOneTimePrice = getLoopSubscriptionContainer(
        productId
    ).querySelector("#loop-price-one-time");
    if (loopOneTimePrice) {
        loopOneTimePrice.innerHTML = loopFormatMoney(variant.price, true);
    }
}

/**
 *************** Utility functions ********************
 */

/**
 * log a message
 * @param {string} message
 */
function log(message) {
    widgetLogger(message);
}

/**
 * log error message
 * @param {Error} error
 */
function logError(error) {
    widgetLogger(error);
}

/**
 * initialize global loop data object
 * @param {number} productId
 */
function initializeLoopData(productId) {
    if (!window.LoopSubscriptions) {
        window.LoopSubscriptions = {};
    }
    window.LoopSubscriptions[productId] = getLoopProductData(productId);
}

/**
 * get product data from html element
 * @param {number} productId
 * @returns
 */
function getLoopProductData(productId) {
    const textData = document.querySelector(`.${productDataClass}-${productId}`)
        .textContent;
    return JSON.parse(textData);
}

/**
 *
 * @param {number} productId
 * @returns
 */
function getLoopSubscriptionContainer(productId) {
    return document.querySelector(
        `#${loopSubscriptionContainerId}-${productId}`
    );
}

/**
 *
 * @returns get current path
 */
function getCurrentPathLoop() {
    const pathParts = window.location.pathname.split("/");
    return pathParts[pathParts.length - 1];
}

/**
 *
 * @returns if touch device
 */
function loopIsTouchDevice() {
    return "ontouchstart" in document.documentElement;
}

/**
 **************** Bundle Functions Start ****************
 */

async function getLoopBundleSpgs(productId) {
    try {
        const spgs =
            loopProps[productId]?.product?.selling_plan_groups?.flatMap(
                (spg) => {
                    return spg?.selling_plans?.map((sp) => ({
                        selling_plan_group_id: spg.id,
                        selling_plan_id: sp.id,
                    }));
                }
            ) || [];

        const shopifySellingPlanIdsToExcludeOnWidget =
            window.loopProps[productId]["storeJson"]
                .shopifySellingPlanIdsToExcludeOnWidget ?? [];
        const bundleShopifySellingPlanIds =
            window.loopProps[productId]["storeJson"]
                .bundleShopifySellingPlanIds ?? [];

        const storeDefaultSellingPlanIds =
            window.loopProps[productId]["storeJson"]
                .storeDefaultSellingPlanShopifyIds ?? [];

        const sps = [...new Set([...bundleShopifySellingPlanIds])];

        let bundleSellingPlanGroupIds = [];
        let nonBundleSellingPlanGroupIds = [];

        let excludedGroupIds = new Set();

        for (const spg of spgs) {
            const isExcludedByWidget = shopifySellingPlanIdsToExcludeOnWidget.includes(
                spg.selling_plan_id
            );
            // KC CUSTOM: Also treat plans not in storeDefaultSellingPlanShopifyIds as excluded
            // This handles the case where Loop dashboard has "Storefront product pages" unchecked
            // but store.json is not updated for custom widget setups
            const isNotInDefaultList = storeDefaultSellingPlanIds.length > 0 &&
                !storeDefaultSellingPlanIds.includes(spg.selling_plan_id);

            if (isExcludedByWidget || isNotInDefaultList) {
                excludedGroupIds.add(spg.selling_plan_group_id);
            } else {
                if (
                    sps.includes(spg.selling_plan_id) &&
                    window.loopProps[productId]?.storeJson?.preferences
                        ?.hideBundleSellingPlansOnProductPage
                ) {
                    bundleSellingPlanGroupIds.push(spg.selling_plan_group_id);
                } else {
                    nonBundleSellingPlanGroupIds.push(spg.selling_plan_group_id);
                }
            }
        }

        // KC CUSTOM: If a group has any plan excluded by widget, treat the whole group as excluded from storefront
        let hideLoopStorefrontExcludedGroupIds = [...excludedGroupIds];

        // Ensure excluded groups are not kept in valid bundle or non-bundle plan lists
        nonBundleSellingPlanGroupIds = nonBundleSellingPlanGroupIds.filter(
            (id) => !excludedGroupIds.has(id)
        );
        bundleSellingPlanGroupIds = bundleSellingPlanGroupIds.filter(
            (id) => !excludedGroupIds.has(id)
        );

        window.loopProps[productId]["bundleSPGS"] = [
            ...new Set(bundleSellingPlanGroupIds),
        ];
        window.loopProps[productId]["nonBundleSPGS"] = [
            ...new Set(nonBundleSellingPlanGroupIds),
        ];
        window.loopProps[productId]["storefrontExcludedSPGS"] = [
            ...new Set(hideLoopStorefrontExcludedGroupIds),
        ];
        hideLoopBundleSPG(productId);
        hideLoopStorefrontExcludedSPG(productId);

        if (window.loopProps[productId].storeJson.hasPrepaid) {
            const allSellingPlanIds =
                window.loopProps[
                    productId
                ]?.product?.selling_plan_groups?.flatMap((spg) => {
                    return spg?.selling_plans?.map((sp) => sp.id);
                }) || [];
            const spidsAsString = allSellingPlanIds.join(",");
            const prepaidUrl = `${GET_PREPAID_SELLING_PLAN_DATA_URL}${spidsAsString}`;

            const prepaidRes = await fetch(prepaidUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const prepaidResJSON = await prepaidRes.json();
            const prepaidData = prepaidResJSON.data;
            window.loopProps[productId]["prepaidSellingPlans"] =
                prepaidData?.sellingPlans || {};
        }
    } catch (error) {
        widgetLogger("getLoopBundleSpgs", error);
    }
}

function hideLoopBundleSPG(productId) {
    if (!isProductBundle(productId)) {
        const loopProps = window.loopProps[productId];
        const loopContainer = getLoopSubscriptionContainer(productId);
        if (loopContainer) {
            const bspgs = loopProps["bundleSPGS"];
            if (
                bspgs?.length &&
                loopProps?.storeJson?.preferences
                    ?.hideBundleSellingPlansOnProductPage
            ) {
                bspgs.forEach((spgId) => {
                    const bundlespg = loopContainer.querySelectorAll(
                        `#loop-selling_plan_group-${spgId}`
                    );
                    if (bundlespg?.length) {
                        bundlespg.forEach((spg) => {
                            spg.classList.add("loop-display-none");
                        });
                    }
                });
                clickUpdatedSPGLoop(productId);
            }
        }
    }
}

function hideLoopStorefrontExcludedSPG(productId) {
    try {
        const loopProps = window.loopProps[productId];
        const loopContainer = getLoopSubscriptionContainer(productId);
        if (loopContainer) {
            const excludedPlans = loopProps["storefrontExcludedSPGS"];
            excludedPlans?.forEach((spgId) => {
                const bundlespg = loopContainer.querySelectorAll(
                    `#loop-selling_plan_group-${spgId}`
                );
                if (bundlespg?.length) {
                    bundlespg.forEach((spg) => {
                        spg.classList.add("loop-display-none");
                    });
                }
            });
            clickUpdatedSPGLoop(productId);
        }
    } catch (error) {
        widgetLogger("error", error);
    }
}

function clickUpdatedSPGLoop(productId) {
    showSellingPlanFieldsetLoop(productId);
    const hasCustomLabel = getLoopSubscriptionContainer(productId)?.querySelector(
        ".loop-purchase-options-label .continuum-desc, .loop-purchase-options-label .continuum-eyebrow"
    );

    if (
        hasCustomLabel || (
            window &&
            window.loopPropsUI &&
            window.loopPropsUI.displayLoopPurchaseOptionLabel
        )
    ) {
        showLoopPurchaseOptionsLabel(productId);
    } else {
        hideLoopPurchaseOptionsLabel(productId);
    }

    const selectedVariantId = getLoopVariantId(productId);
    const selectedVariant = window.loopProps[productId].product.variants.find(
        (v) => v.id == selectedVariantId
    );
    let nonBundleSpgs = window.loopProps[productId]["nonBundleSPGS"] ?? [];
    const bundleSpgs = window.loopProps[productId]["bundleSPGS"] ?? [];
    const variants = window.loopProps[productId].product.variants;
    const variantIdFromUrl =
        getVariantIdFromURLLoop(variants) ??
        getFirstAvailableVariantVariantIdLoop(productId);
    const variant =
        variants.find((v) => v.id == variantIdFromUrl) ?? variants[0];
    const variantSpgs = variant.selling_plan_allocations.map(
        (spg) => spg.selling_plan_group_id
    );
    nonBundleSpgs = nonBundleSpgs.filter((spg) => variantSpgs.includes(spg));

    const loopContainer = getLoopSubscriptionContainer(productId);
    const themeDefaultToSubscription = loopContainer ? loopContainer.dataset.defaultToSubscription === 'true' : false;
    const shouldDefaultToSub = themeDefaultToSubscription || window.loopPropsUI.byDefaultChooseSubscriptionOption;

    if (isProductBundle(productId)) {
        if (
            bundleSpgs.length !== 0 &&
            shouldDefaultToSub
        ) {
            showLoopWidget(productId);
            const firstAvailableBundleSpg = bundleSpgs[0];
            const spgDiv = getLoopSubscriptionContainer(
                productId
            ).querySelectorAll(
                `#loop-selling_plan_group-${firstAvailableBundleSpg}`
            );
            spgDiv.forEach((node) => {
                const childNodes = node.querySelectorAll(
                    `#loop-allocation-${firstAvailableBundleSpg}`
                );
                childNodes.forEach((ele) => {
                    const targetNodes = ele.querySelectorAll(
                        `#loop-${selectedVariantId}-${firstAvailableBundleSpg}`
                    );
                    targetNodes.forEach((targetNode) => {
                        targetNode.click();
                    });
                });
            });
        } else {
            setTimeout(
                () =>
                    defaultSelectOneTimePurchaseOptionLoop(
                        selectedVariant,
                        productId
                    ),
                500
            );
        }
    } else if (
        nonBundleSpgs.length !== 0 &&
        shouldDefaultToSub
    ) {
        showLoopWidget(productId);
        const firstAvailableNonBundleSpg = nonBundleSpgs[0];
        const spgDiv = getLoopSubscriptionContainer(productId).querySelectorAll(
            `#loop-selling_plan_group-${firstAvailableNonBundleSpg}`
        );
        spgDiv.forEach((node) => {
            const childNodes = node.querySelectorAll(
                `#loop-allocation-${firstAvailableNonBundleSpg}`
            );
            childNodes.forEach((ele) => {
                const targetNodes = ele.querySelectorAll(
                    `#loop-${selectedVariantId}-${firstAvailableNonBundleSpg}`
                );
                targetNodes.forEach((targetNode) => {
                    targetNode.click();
                });
            });
        });
    } else {
        if (nonBundleSpgs.length === 0) {
            hideLoopWidget(productId);
        }
        setTimeout(
            () =>
                defaultSelectOneTimePurchaseOptionLoop(
                    selectedVariant,
                    productId
                ),
            500
        );
    }
}

function showLoopWidget(productId) {
    const loopWidgetContainer = document.getElementById(
        `loop-subscription-container-${productId}`
    );

    if (loopWidgetContainer) {
        loopWidgetContainer.style.display = "block";
    }
}

function showLoopSkeletonLoader(productId) {
    const loopWidgetSkeleton = document.getElementById(
        `loop-widget-skeleton-loader-container-id-${productId}`
    );

    if (loopWidgetSkeleton) {
        loopWidgetSkeleton.style.display = "block";
    }
}

function hideLoopSkeletonLoader(productId) {
    const skeletons = document.querySelectorAll(
        ".loop-widget-skeleton-loader-container"
    );

    if (skeletons.length > 0) {
        skeletons.forEach((skeleton) => {
            if (skeleton) {
                skeleton.style.display = "none";
            }
        });
    }
}

function hideLoopWidget(productId) {
    const loopWidgetContainer = document.getElementById(
        `loop-subscription-container-${productId}`
    );
    if (loopWidgetContainer) {
        loopWidgetContainer.style.display = "none";
    }
}

function isProductBundle(productId) {
    const storeJson = window.loopProps[productId]["storeJson"];
    return !!(
        storeJson &&
        storeJson.hasPresetBundles &&
        storeJson.presetBundleShopifyProductIds.includes(Number(productId))
    );
}

async function getBundleDataByProductId(productId) {
    const storeJson = window.loopProps[productId]["storeJson"];
    if (isProductBundle(productId)) {
        const presetUrl = `${baseUrl}/presetBundles/${productId}.json`;
        const presetRes = await fetchWithCacheControl(presetUrl, "preset");
        const productBundleData = await presetRes.json();
        window.loopProps[productId]["productBundleData"] = {
            ...productBundleData,
        };
        return productBundleData;
    } else {
        if (!storeJson) {
            widgetLogger("storeJson is not defined");
            return;
        }
        if (!storeJson.hasPresetBundles) {
            widgetLogger("No preset bundles");
            return;
        }
        if (!storeJson.presetBundleShopifyProductIds.includes(productId)) {
            widgetLogger("Product is not a preset bundle");
            return;
        }
    }
}

function handleBundleWidgetVisibility(productId) {
    if (
        window.loopProps[productId].productBundleData.purchaseType ===
        "SUBSCRIPTION"
    ) {
        hideOneTimePurchaseOptionLoop(productId);
        const selectedVariantId = getLoopVariantId(productId);
        const selectedVariant = window.loopProps[
            productId
        ].product.variants.find((v) => v.id == selectedVariantId);
        defaultSelectFirstSellingPlanLoop(selectedVariant, productId);
    } else if (
        window.loopProps[productId].productBundleData.purchaseType === "ONETIME"
    ) {
        // hide widget
        hideLoopSellingPlanFieldset(productId);
        hideLoopPurchaseOptionsLabel(productId);
        defaultSelectOneTimePurchaseOptionLoop(null, productId);
    }
}

// override product page btn
async function processLoopBundleProduct(productId) {
    try {
        const productBundleData = await getBundleDataByProductId(productId);
        if (productBundleData) {
            overrideAddToCartButtonLoop(productId, productBundleData.variants);
            handleBundleWidgetVisibility(productId);
            applyLoopBundleDiscount(productId);
            checkAllowCheckoutIfBundle(productId);
        }
    } catch (error) {
        widgetLogger("processLoopBundleProduct", error);
    }
}

function checkAllowCheckoutIfBundle(productId) {
    enableAddToCartBtnLoop(productId);
    const selectedVariantId = window.loopProps[productId].selectedVariantId;
    if (isProductBundle(productId) && selectedVariantId) {
        const selectedVariant = getLoopWidgetBundleVariantInfo(
            productId,
            selectedVariantId
        );
        const allowCheckout = selectedVariant.mappedProductVariants.length > 0;
        const outOfStock = selectedVariant?.outOfStock;

        if (outOfStock) {
            const buttonText =
                window?.loopPropsUI?.translationData
                    ?.widget_out_of_stock_text || "Out of Stock";
            const parentElement =
                document.querySelector(
                    `[data-loop-product-id="${productId}"]`
                ) || document;
            const addToCartButton = getAddToCartButtonLoop(parentElement);
            if (addToCartButton) {
                setTimeout(() => {
                    updateButtonInnerHTMLLoop(addToCartButton, buttonText);
                }, 500);
            }
        }

        if (!allowCheckout || outOfStock) {
            disableAddToCartBtnLoop(productId);
        }
    }
}

function getBundleVariantDiscountInfo(
    productId,
    selectedVariantId,
    selectedSellingPlanId
) {
    const bundleVariant = getLoopWidgetBundleVariantInfo(
        productId,
        selectedVariantId
    );
    const discount = selectedSellingPlanId
        ? bundleVariant.mappedDiscounts.find(
            (d) => d.purchaseType === "SUBSCRIPTION"
        )
        : bundleVariant.mappedDiscounts.find(
            (d) => d.purchaseType !== "SUBSCRIPTION"
        );
    return discount;
}

function applyLoopBundleDiscount(productId) {
    if (!isProductBundle(productId)) {
        return;
    }

    let variant = findSelectedVariantLoop(
        productId,
        window.loopProps[productId].selectedVariantId
    );
    let selectedVariantId = window.loopProps[productId].selectedVariantId;
    const bundleVariantInfo = getLoopWidgetBundleVariantInfo(
        productId,
        selectedVariantId
    );
    if (!bundleVariantInfo?.mappedDiscounts?.length) {
        return;
    }
    const conversionRate = window.Shopify.currency.rate;
    let sellingPlan =
        window.loopProps && window.loopProps[productId]
            ? window.loopProps[productId].sellingPlan
            : {};
    const product = window.loopProps[productId]?.product || {};
    const { selling_plan_groups } = product;
    const { selling_plan_allocations } = variant;

    selling_plan_groups.map((spg) => {
        if (Array.isArray(spg.selling_plans) && spg.selling_plans.length) {
            let firstSellingPlan = spg.selling_plans[0];
            let sellingPlanAllocation =
                selling_plan_allocations.find(
                    (a) =>
                        Number(a.selling_plan_id) ===
                        Number(firstSellingPlan.id)
                ) || {};
            const {
                selling_plan_group_id,
                price,
                compare_at_price,
            } = sellingPlanAllocation;
            const originaPrice = useCompareAtPrice
                ? compare_at_price || variant.price
                : price;
            let element = document.querySelector(
                `#loop-price-${variant.id}-${selling_plan_group_id}`
            );
            let elementOriginal = document.querySelector(
                `#loop-price-original-${variant.id}-${selling_plan_group_id}`
            );

            if (elementOriginal) {
                elementOriginal.innerHTML = loopFormatMoney(originaPrice, true);
                elementOriginal.classList.remove("loop-display-none");
                elementOriginal.style.textDecorationLine = "line-through";
            }
            if (element) {
                const bundlePrice =
                    bundleVariantInfo.sellingPlanPrices[firstSellingPlan.id] *
                    100 *
                    conversionRate;
                // KC CUSTOM: what the checkout charges, the plan's Duos per delivery included
                element.innerHTML = loopFormatMoney(bundlePrice * kcPlanUnits(firstSellingPlan.id), true);

                let totalDiscount = Math.round(
                    ((originaPrice - bundlePrice) / originaPrice) * 100
                );
                const discountEle = document.querySelector(
                    `#loop-discount-badge-${variant.id}-${selling_plan_group_id}`
                );
                let discountText = `${totalDiscount > 0 ? totalDiscount : 0}%`;
                let text = window?.loopPropsUI?.discountBadgeText || " ";
                let discountLabelText = text.replace(
                    `{discount_value}`,
                    discountText
                );
                discountEle.innerHTML = `${discountLabelText}`;
                if (
                    totalDiscount > 0 &&
                    window.loopPropsUI.displayDiscountBadge
                ) {
                    discountEle.classList.remove("loop-display-none");
                } else {
                    discountEle.classList.add("loop-display-none");
                }
            }

            if (
                elementOriginal &&
                element?.innerHTML == elementOriginal?.innerHTML
            ) {
                elementOriginal.classList.add("loop-display-none");
            }
        }
    });

    if (sellingPlan && sellingPlan.selling_plan_group_id) {
        const { selling_plan_group_id, price, compare_at_price } = sellingPlan;
        const originaPrice = useCompareAtPrice
            ? compare_at_price || variant.price
            : price;
        let element = document.querySelector(
            `#loop-price-${variant.id}-${selling_plan_group_id}`
        );
        let elementOriginal = document.querySelector(
            `#loop-price-original-${variant.id}-${selling_plan_group_id}`
        );

        if (elementOriginal) {
            elementOriginal.innerHTML = loopFormatMoney(originaPrice, true);
            elementOriginal.classList.remove("loop-display-none");
            elementOriginal.style.textDecorationLine = "line-through";
        }
        if (element) {
            const bundlePrice =
                bundleVariantInfo.sellingPlanPrices[
                sellingPlan.selling_plan_id
                ] *
                100 *
                conversionRate;
            // KC CUSTOM: what the checkout charges, the plan's Duos per delivery included
            element.innerHTML = loopFormatMoney(bundlePrice * kcPlanUnits(sellingPlan.selling_plan_id), true);

            let totalDiscount = Math.round(
                ((originaPrice - bundlePrice) / originaPrice) * 100
            );
            const discountEle = document.querySelector(
                `#loop-discount-badge-${variant.id}-${selling_plan_group_id}`
            );
            let discountText = `${totalDiscount > 0 ? totalDiscount : 0}%`;
            let text = window?.loopPropsUI?.discountBadgeText || " ";
            let discountLabelText = text.replace(
                `{discount_value}`,
                discountText
            );
            discountEle.innerHTML = `${discountLabelText}`;
            if (totalDiscount > 0 && window.loopPropsUI.displayDiscountBadge) {
                discountEle.classList.remove("loop-display-none");
            } else {
                discountEle.classList.add("loop-display-none");
            }
        }
        if (
            elementOriginal &&
            element?.innerHTML == elementOriginal?.innerHTML
        ) {
            elementOriginal.classList.add("loop-display-none");
        }
    }

    const originaPriceOnetime = useCompareAtPrice
        ? variant.compare_at_price || variant.price
        : variant.price;
    const bundlePriceOnetimeDiscounted = bundleVariantInfo.oneTimePrice;
    let loopOneTimePrice = getLoopSubscriptionContainer(
        productId
    ).querySelector("#loop-price-one-time");
    let loopOneTimePriceOriginal = getLoopSubscriptionContainer(
        productId
    ).querySelector("#loop-price-one-time-original");
    let bundlePriceOnetime = originaPriceOnetime;
    if (loopOneTimePrice) {
        if (bundlePriceOnetimeDiscounted) {
            bundlePriceOnetime =
                bundlePriceOnetimeDiscounted * 100 * conversionRate;
        }
        loopOneTimePrice.innerHTML = loopFormatMoney(bundlePriceOnetime, true);
    }
    if (loopOneTimePriceOriginal) {
        let val = originaPriceOnetime;
        loopOneTimePriceOriginal.innerHTML = loopFormatMoney(val, true);
        loopOneTimePriceOriginal.classList.remove("loop-display-none");
        loopOneTimePriceOriginal.style.textDecorationLine = "line-through";

        if (bundlePriceOnetimeDiscounted) {
            let totalDiscount = Math.round(
                ((originaPriceOnetime -
                    bundlePriceOnetimeDiscounted * 100 * conversionRate) /
                    originaPriceOnetime) *
                100
            );
            const discountEle = document.querySelector(
                `#loop-discount-badge-onetime`
            );
            let discountText = `${totalDiscount > 0 ? totalDiscount : 0}%`;
            let text = window?.loopPropsUI?.discountBadgeText || " ";
            let discountLabelText = text.replace(
                `{discount_value}`,
                discountText
            );
            discountEle.innerHTML = `${discountLabelText}`;
            if (totalDiscount > 0) {
                // discountEle.classList.remove("loop-display-none");
            }
        }
    }
    if (
        loopOneTimePrice &&
        loopOneTimePriceOriginal &&
        loopOneTimePrice.innerHTML == loopOneTimePriceOriginal.innerHTML
    ) {
        loopOneTimePriceOriginal.classList.add("loop-display-none");
    }
}

function listenLoopCustomEvent() {
    document.addEventListener(
        "loopPresetAddToCartSuccessEvent",
        function (event) {
            const productId = event.detail.productId;
            const response = event.detail.response;
            // KC CUSTOM: the cart in the shopper's language; /cart goes on to checkout (sections/kc-cart-redirect.liquid)
            window.location.href = `${window.Shopify.routes.root}cart`;
            widgetLogger(
                `Loop Product ${productId} added to cart. Response:`,
                response
            );
        }
    );
}

function removeAllEventListenersLoop(element) {
    const clone = element.cloneNode(true);
    element.parentNode.replaceChild(clone, element);
    return clone;
}

function overrideAddToCartButtonLoop(productId) {
    const productForms = document.querySelectorAll(
        `[data-loop-product-id="${productId}"]`
    );
    productForms.forEach((form) => {
        let submitButtons = form.querySelectorAll("button[type=submit]");
        if (submitButtons.length === 0) {
            submitButtons = form.querySelectorAll("button");
        }
        submitButtons.forEach((btn) => {
            btn = removeAllEventListenersLoop(btn);
            btn.addEventListener("click", (event) => {
                const quantity = document.querySelector(
                    PRODUCT_QUANTITY_SELECTOR
                );
                loopHandleAddToCart(event, productId, quantity?.value);
            });
        });
    });
}

function hideOneTimePurchaseOptionLoop(productId) {
    document
        .querySelectorAll(`form[data-loop-product-id="${productId}"]`)
        .forEach((form) => {
            form.querySelectorAll(".loop-one-time-purchase-option").forEach(
                (option) => {
                    option.classList.add("loop-display-none");
                }
            );
        });
}

function disableAddToCartBtnLoop(productId) {
    const productForms = document.querySelectorAll(
        `[data-loop-product-id="${productId}"]`
    );
    productForms.forEach((form) => {
        const submitButtons = form.querySelectorAll("button");
        submitButtons.forEach((btn) => {
            btn.disabled = true;
        });
    });
}

function enableAddToCartBtnLoop(productId) {
    const productForms = document.querySelectorAll(
        `[data-loop-product-id="${productId}"]`
    );
    productForms.forEach((form) => {
        const submitButtons = form.querySelectorAll("button");
        submitButtons.forEach((btn) => {
            btn.disabled = false;
        });
    });
}

function getSelectedSellingPlanLoop(productId) {
    let sp = null;
    document
        .querySelectorAll(`form[data-loop-product-id="${productId}"]`)
        .forEach((form) => {
            const existingInput = form.querySelector(
                'input[name="selling_plan"]'
            );
            if (existingInput?.value) {
                sp = Number(existingInput.value);
            }
        });
    return sp;
}

function getLoopWidgetProductBundleData(productId) {
    return window.loopProps[productId]?.productBundleData ?? {};
}

function getLoopWidgetBundleVariantInfo(productId, variantId) {
    const productBundleData = getLoopWidgetProductBundleData(productId);
    return productBundleData?.variants?.find(
        (v) => v.shopifyId === Number(variantId)
    );
}

async function loopWidgetCreateBundleTransaction(productId, payload) {
    try {
        const authorization =
            window.loopProps[productId].storeJson.sentinalAuthToken;
        const response = await fetch(
            `${window.loopProps[productId].storeJson.apiUrl.bundleTransaction}`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    authorization: authorization,
                },
                body: JSON.stringify(payload),
            }
        );
        const responseJson = await response.json();
        return responseJson.data.transactionId;
    } catch (error) {
        widgetLogger("loopWidgetCreateBundleTransaction", error);
        throw error;
    }
}

function loopWidgetCreateBundleTransactionPayload(
    productId,
    quantity,
    selectedSellingPlanId
) {
    const selectedVariantId = getLoopWidgetVariantId(productId);
    const bundleVariant = getLoopWidgetBundleVariantInfo(
        productId,
        selectedVariantId
    );
    if (!bundleVariant) return { payload: null, bundleVariantDiscount: null };

    const discount = selectedSellingPlanId
        ? bundleVariant.mappedDiscounts.find(
            (d) => d.purchaseType === "SUBSCRIPTION"
        )
        : bundleVariant.mappedDiscounts.find(
            (d) => d.purchaseType !== "SUBSCRIPTION"
        );

    if (!discount) return { payload: null, bundleVariantDiscount: null };

    return {
        payload: {
            presetProductShopifyId: Number(productId),
            presetDiscountId: discount.id,
            presetVariantShopifyId: Number(selectedVariantId),
            totalQuantity: Number(quantity),
            sellingPlanShopifyId: Number(selectedSellingPlanId),
        },
        bundleVariantDiscount: discount,
    };
}

async function handleBundleTransactionLoopWidget(
    productId,
    quantity,
    selectedSellingPlanId
) {
    try {
        const {
            payload,
            bundleVariantDiscount,
        } = loopWidgetCreateBundleTransactionPayload(
            productId,
            quantity,
            selectedSellingPlanId
        );
        if (!payload)
            return { bundleTransactionId: null, bundleVariantDiscount: null };

        const bundleTransactionId = await loopWidgetCreateBundleTransaction(
            productId,
            payload
        );
        if (!bundleTransactionId) enableAddToCartBtnLoop(productId);
        return { bundleTransactionId, bundleVariantDiscount };
    } catch (error) {
        enableAddToCartBtnLoop(productId);
        widgetLogger("handleBundleTransactionLoopWidget", error);
        return { bundleTransactionId: null, bundleVariantDiscount: null };
    }
}

async function getLoopWidgetBundleDiscountAttributes() {
    try {
        const url = `https://${window.Shopify.cdnHost.split("/cdn")[0]
            }/cart.json`;
        const res = await (await fetch(url)).json();
        const loopBundleDiscountAttributes = res.attributes
            ?._loopBundleDiscountAttributes
            ? JSON.parse(res.attributes._loopBundleDiscountAttributes)
            : {};

        const bundleIdsInCart = new Set(
            res.items
                .map(
                    (item) =>
                        item.properties?._bundleId ||
                        item.properties?._loopBundleTxnId
                )
                .filter(Boolean)
        );

        return Object.keys(loopBundleDiscountAttributes)
            .filter((key) => bundleIdsInCart.has(key))
            .reduce((obj, key) => {
                obj[key] = loopBundleDiscountAttributes[key];
                return obj;
            }, {});
    } catch (error) {
        widgetLogger("getLoopWidgetBundleDiscountAttributes", error);
        return {};
    }
}

async function loopWidgetCreateAddToCartPayload(
    productId,
    bundleTransactionId,
    bundleVariantDiscount,
    selectedSellingPlanId,
    selectedBundleVariantId,
    quantity,
    productBundleData
) {
    // KC CUSTOM: the "Subscription Name" cart attribute Loop Loyalty needs for Care Credit, from the
    // selling plan ID (assets/kc-plan-select.js), never from the visible plan name
    const kcSubscriptionName = selectedSellingPlanId && window.KCPlanSelect
        ? window.KCPlanSelect.subscriptionName(selectedSellingPlanId)
        : "";
    const formData = {
        items: [],
        attributes: {
            _loopBundleDiscountAttributes: {},
            ...(kcSubscriptionName && selectedSellingPlanId ? {
                'Subscription Name': kcSubscriptionName
            } : {}),
        },
    };

    const oldAttr = await getLoopWidgetBundleDiscountAttributes();
    const currentDiscountAttribute = {
        [bundleTransactionId]: {
            discountType: bundleVariantDiscount.type,
            discountValue: bundleVariantDiscount.value,
            discountComputedValue: bundleVariantDiscount
                ? selectedSellingPlanId
                    ? bundleVariantDiscount.sellingPlanComputedDiscounts[
                    selectedSellingPlanId
                    ] *
                    (window.loopProps[productId].storeJson?.preferences
                        ?.presetDummySkuEnabled
                        ? 1
                        : quantity)
                    : bundleVariantDiscount.oneTimeDiscount *
                    (window.loopProps[productId].storeJson?.preferences
                        ?.presetDummySkuEnabled
                        ? 1
                        : quantity)
                : 0,
        },
    };

    formData.attributes._loopBundleDiscountAttributes = JSON.stringify({
        ...oldAttr,
        ...currentDiscountAttribute,
    });

    const selectedBundleVariant = getLoopWidgetBundleVariantInfo(
        productId,
        selectedBundleVariantId
    );
    const selectedBundleVariantProducts =
        selectedBundleVariant?.mappedProductVariants ?? [];

    if (
        window.loopProps[productId].storeJson?.preferences
            ?.presetDummySkuEnabled
    ) {
        const obj = {
            id: selectedBundleVariantId,
            quantity: quantity,
            selling_plan: selectedSellingPlanId,
            properties: {
                _loopBundleTxnId: bundleTransactionId,
                _isPresetBundleProduct: true,
                ...(window.loopProps[productId].storeJson.preferences
                    .showBundleName
                    ? { bundleName: productBundleData.name ?? "" }
                    : { _bundleName: productBundleData.name ?? "" }),
            },
        };
        formData.items.push(obj);
    } else {
        if (selectedBundleVariantProducts.length) {
            selectedBundleVariantProducts.forEach((childProduct) => {
                const obj = {
                    id: childProduct.shopifyId,
                    quantity: childProduct.quantity * quantity,
                    selling_plan: selectedSellingPlanId,
                    properties: {
                        _bundleId: bundleTransactionId,
                        _isPresetBundleProduct: true,
                        ...(window.loopProps[productId].storeJson.preferences
                            .showBundleName
                            ? { bundleName: productBundleData.name ?? "" }
                            : { _bundleName: productBundleData.name ?? "" }),
                    },
                };
                formData.items.push(obj);
            });
        }
    }

    return formData;
}

async function shopifyAddToCartByLoopWidget(payload, productId) {
    const endpoint = `${window.Shopify.routes.root}cart/add.js`;
    try {
        const kcSellingPlanId = payload?.items?.[0]?.selling_plan || "";
        const kcManaged = Boolean(window.KCPlanSelect && window.KCPlanSelect.manages(productId));
        // KC CUSTOM: a plan product's subscription brings its Welcome Kit in the same add, nested under the
        // plan line in cart order (assets/kc-plan-select.js nestKit)
        const kcKit = kcManaged && kcSellingPlanId ? window.KCPlanSelect.kitLines(productId) : [];
        const kcPayload = kcKit.length
            ? { ...payload, items: window.KCPlanSelect.nestKit(payload.items.concat(kcKit)) }
            : payload;
        // KC CUSTOM: one purchase choice per plan product: earlier lines and kit lines go first, one
        // replacement at a time, the earlier choice back if the add is rejected (assets/kc-plan-select.js)
        const addToCart = () => fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(kcPayload),
        });
        // KC CUSTOM: after a successful add of a plan product, as the buybox CTA (assets/karmi-add-to-cart.js):
        // the cart's "Subscription Name", then checkout (no theme cart), with the "Welcome Kit" code for the
        // kit added here. replaceChoice goes there only when no later choice is waiting.
        const kcFinish = async () => {
            await window.KCPlanSelect.syncCartSubscriptionName(kcSellingPlanId).catch(() => {});
            const checkout = `${window.Shopify.routes.root}checkout`;
            return kcKit.length ? `${checkout}?discount=Welcome%20Kit` : checkout;
        };
        const response = kcManaged
            ? await window.KCPlanSelect.replaceChoice(productId, kcSellingPlanId, addToCart, kcFinish)
            : await addToCart();
        const data = await response.json();
        // KC CUSTOM: a rejected add is no success: the earlier choice is back in the cart (replaceChoice)
        // and nothing goes to the cart or checkout
        if (!response.ok) {
            widgetLogger("shopifyAddToCartByLoopWidget", data);
            enableAddToCartBtnLoop(productId);
            return;
        }
        // KC CUSTOM: a plan product is on its way to checkout (kcFinish), or a later choice takes over
        if (kcManaged) {
            if (!response.kcNavigating) enableAddToCartBtnLoop(productId);
            return;
        }
        // KC CUSTOM: the cart's "Subscription Name" follows the subscriptions now in it (assets/kc-plan-select.js)
        if (window.KCPlanSelect) {
            await window.KCPlanSelect.syncCartSubscriptionName(kcSellingPlanId).catch(() => {});
        }
        await dispatchLoopWidgetEvent(
            productId,
            "loopPresetAddToCartSuccessEvent",
            data
        );
    } catch (error) {
        widgetLogger("shopifyAddToCartByLoopWidget", error);
        enableAddToCartBtnLoop(productId);
    }
}

function getLoopWidgetVariantId(productId) {
    return Number(window.loopProps[productId].selectedVariantId);
}

async function loopHandleAddToCart(event, productId, quantity = 1) {
    event.preventDefault();
    event.stopPropagation();
    disableAddToCartBtnLoop(productId);

    const bundleVariant = getLoopWidgetBundleVariantInfo(
        productId,
        getLoopWidgetVariantId(productId)
    );
    if (
        !bundleVariant ||
        bundleVariant.outOfStock ||
        bundleVariant.mappedProductVariants.length === 0
    ) {
        return;
    }

    const productBundleData = getLoopWidgetProductBundleData(productId);
    const selectedSellingPlanId = getSelectedSellingPlanLoop(productId);
    // KC CUSTOM: a plan product's subscription adds its plan's Duos per delivery (assets/kc-plan-select.js)
    if (window.KCPlanSelect) {
        quantity = window.KCPlanSelect.quantityFor(productId, selectedSellingPlanId || "", quantity);
    }

    const {
        bundleTransactionId,
        bundleVariantDiscount,
    } = await handleBundleTransactionLoopWidget(
        productId,
        quantity,
        selectedSellingPlanId
    );
    if (!bundleTransactionId) {
        return;
    }

    const payload = await loopWidgetCreateAddToCartPayload(
        productId,
        bundleTransactionId,
        bundleVariantDiscount,
        selectedSellingPlanId,
        getLoopWidgetVariantId(productId),
        quantity,
        productBundleData
    );
    await shopifyAddToCartByLoopWidget(payload, productId);
}

async function dispatchLoopWidgetEvent(productId, eventName, response) {
    const addToCartEvent = new CustomEvent(eventName, {
        detail: { productId, response },
    });
    document.dispatchEvent(addToCartEvent);
}

/**
 **************** Bundle Functions Ends ****************
 */

/**
 **************** Prepaid Functions Start ****************
 */

function getFirstVariantPlanFromGroup(variantPlans, groupPlans) {
    const variantIds = variantPlans.map((vp) => vp.selling_plan_id);
    let planToReturn = null;
    for (let i = 0; i < groupPlans.length; i++) {
        const vPlan = groupPlans[i];
        if (variantIds.includes(vPlan.id)) {
            planToReturn = vPlan;
            break;
        }
    }
    if (!planToReturn) {
        planToReturn = groupPlans[0];
    }
    return planToReturn;
}

function updatePrepaidPriceInUILoop({ productId }) {
    const prepaidSellingPlans =
        window.loopProps[productId]["prepaidSellingPlans"];
    if (!window.loopProps[productId]?.prepaidSellingPlans) {
        return;
    }

    let variant = findSelectedVariantLoop(
        productId,
        window.loopProps[productId].selectedVariantId
    );

    let sellingPlan =
        window.loopProps && window.loopProps[productId]
            ? window.loopProps[productId].sellingPlan
            : {};
    const product = window.loopProps[productId]?.product || {};
    const { selling_plan_groups } = product;
    const { selling_plan_allocations } = variant;
    selling_plan_groups.map((spg) => {
        if (Array.isArray(spg.selling_plans) && spg.selling_plans.length) {
            // let firstSellingPlan = spg.selling_plans[0];
            // KC: the card's selected, offered, allowed plan (kcCardAllocation), else Loop's first one
            const kcCard = kcCardAllocation(productId, variant, spg.id);
            let firstSellingPlan = kcCard
                ? { id: Number(kcCard.selling_plan_id) }
                : getFirstVariantPlanFromGroup(
                    selling_plan_allocations,
                    spg.selling_plans
                );
            if (
                !prepaidSellingPlans[firstSellingPlan.id] ||
                !prepaidSellingPlans[firstSellingPlan.id]?.isPrepaidV2
            ) {
                return;
            }
            const deliveriesPerBillingCycle =
                prepaidSellingPlans[firstSellingPlan.id]
                    ?.deliveriesPerBillingCycle || 1;
            let sellingPlanAllcotion =
                selling_plan_allocations.find(
                    (a) =>
                        Number(a.selling_plan_id) ===
                        Number(firstSellingPlan.id)
                ) || {};
            const { selling_plan_group_id } = sellingPlanAllcotion;
            // KC: what the checkout charges (Shopify's price per quantity x quantity)
            const price = kcPlanPrice(sellingPlanAllcotion);
            let element = document.querySelector(
                `#loop-price-${variant.id}-${selling_plan_group_id}`
            );
            if (element) {
                element.innerHTML = loopFormatMoney(price, true);
            }
        }
    });

    if (sellingPlan && sellingPlan.selling_plan_group_id) {
        const { selling_plan_group_id } = sellingPlan;
        // KC: what the checkout charges (Shopify's price per quantity x quantity)
        const price = kcPlanPrice(sellingPlan);
        if (
            !prepaidSellingPlans[sellingPlan.selling_plan_id] ||
            !prepaidSellingPlans[sellingPlan.selling_plan_id]?.isPrepaidV2
        ) {
            return;
        }
        let element = document.querySelector(
            `#loop-price-${variant.id}-${selling_plan_group_id}`
        );
        if (element) {
            element.innerHTML = loopFormatMoney(price, true);
        }
    }
}

function hideDifferentVariantSellingPlansLoop(productData) {
    productData.variants.forEach((variant) => {
        var sellingPlanIds = variant.selling_plan_allocations.map(
            (allocation) => allocation.selling_plan_id.toString()
        );
        variant.selling_plan_allocations.forEach((allocation) => {
            const selectEle = document.querySelector(
                `#loop-select-${variant.id}-${allocation.selling_plan_group_id}`
            );
            if (selectEle) {
                const options = selectEle.querySelectorAll(
                    "option.loop-selling-plan-selector-option"
                );
                options.forEach(function (option) {
                    if (!sellingPlanIds.includes(option.value)) {
                        option.remove();
                    }
                });
            }
        });
    });
}
/**
 **************** Prepaid Functions Ends ****************
 */
